/**
* UI Utilities Module
* Handles notifications, modals, navigation, and general UI interactions
*/

(function ()
{
    'use strict';

    // Global UI state
    let isDashboardVisible = false;

    /**
    * Show notification to user with customizable styling and behavior
    * 
    * This function creates and displays a notification message to the user with
    * different types (success, error, info, warning) and customizable duration.
    * The notification auto-removes after the specified duration. It uses smooth 
    * animations for better user experience.
    * 
    * Features:
    * - Multiple notification types with different colors
    * - Customizable duration and message
    * - Smooth slide-in and slide-out animations (fade effects removed)
    * - Auto-removal after specified duration (default: 3 seconds)
    * - Removes existing notifications before showing new ones
    * - Responsive design with proper positioning
    * 
    * @param {string} message - The message text to display to the user
    * @param {string} type - The type of notification ('success', 'error', 'info', 'warning')
    * @param {number} duration - Duration in milliseconds before auto-removal (default: 3000)
    * @returns {void} No return value
    */
    function showNotification(message, type = 'info', duration = 3000)
    {
        // Cache window.innerWidth once to avoid forced reflow
        const isMobile = window.innerWidth <= 768;

        // Remove existing notifications to prevent overlap
        const existingNotifications = document.querySelectorAll('.notification');
        existingNotifications.forEach(notification => notification.remove());

        // Create notification element with base styling
        const notification = document.createElement('div');
        notification.className = `notification notification-${type}`;

        notification.style.cssText = `
        position: fixed;
        top: ${isMobile ? '70px' : '80px'};
        right: ${isMobile ? '10px' : '20px'};
        left: ${isMobile ? '10px' : 'auto'};
        padding: ${isMobile ? '12px 15px' : '15px 20px'};
        border-radius: 8px;
        color: white;
        font-weight: 600;
        z-index: 10002;
        max-width: ${isMobile ? 'calc(100vw - 20px)' : '400px'};
        word-wrap: break-word;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        animation: slideInRight 0.3s ease-out;
        font-size: ${isMobile ? '0.9rem' : '1rem'};
        line-height: 1.4;
        `;

        // Set background color based on notification type
        const colors = {
            success: '#28a745',
            error: '#dc3545',
            warning: '#ffc107',
            info: '#17a2b8'
        };
        notification.style.backgroundColor = colors[type] || colors.info;

        // Use textContent (not innerHTML) to prevent any HTML injection, including close buttons
        // All notifications auto-remove after duration - no 'x' button needed
        notification.textContent = message;

        document.body.appendChild(notification);

        // Auto-remove notification after specified duration
        setTimeout(() =>
        {
            if (notification.parentElement)
            {
                notification.style.animation = 'slideOutRight 0.3s ease-in';
                setTimeout(() => notification.remove(), 300);
            }
        }, duration);
    }

    /**
    * Close modal by ID
    * @param {string} modalId - The ID of the modal to close
    */
    function closeModal(modalId)
    {
        safeCloseModal(modalId);
    }

    /**
    * Handle navigation clicks with smooth scrolling and state management
    * 
    * This function handles navigation clicks throughout the application, providing
    * smooth scrolling to target sections, mobile menu management, and dashboard
    * state handling. It ensures proper UI state transitions and provides visual
    * feedback for navigation actions.
    * 
    * Features:
    * - Smooth scrolling to target sections
    * - Mobile menu auto-close on navigation
    * - Dashboard state management
    * - Header offset calculation for proper positioning
    * - Console logging for debugging
    * - Section visibility management
    * 
    * @param {string} section - The section ID to navigate to
    * @returns {void} No return value
    */
    function handleNavClick(section)
    {
        console.log('🎯 Navigation clicked:', section);

        // Close mobile menu if it's currently open
        const mobileNav = document.querySelector('.mobile-nav');
        if (mobileNav && mobileNav.classList.contains('active')) 
        {
            mobileNav.classList.remove('active');
        }

        // Handle dashboard visibility state
        if (isDashboardVisible && section !== 'dashboard')
        {
            hideDashboard();
            // Show all sections when leaving dashboard
            showAllSections();
        }

        // Scroll to the target section with smooth animation
        // Use requestAnimationFrame to batch layout reads
        requestAnimationFrame(() =>
        {
            const targetSection = document.getElementById(section);
            if (targetSection)
            {
                // Batch layout reads
                const headerHeight = getHeaderHeight();
                const targetPosition = targetSection.offsetTop - headerHeight - 20;

                window.scrollTo(
                    {
                        top: targetPosition,
                        behavior: 'smooth'
                    });

                // Immediately highlight the clicked section
                highlightSection(section);

                // Update navigation highlighting after scroll completes
                setTimeout(() =>
                {
                    updateNavigationHighlight();
                }, 100);
            }
        });
    }

    /**
    * Hide all main content sections
    */
    function hideAllSections()
    {
        const sections = ['home', 'uber', 'services', 'webshop', 'contact', 'dashboard'];
        sections.forEach(sectionId =>
        {
            const section = document.getElementById(sectionId);
            if (section)
            {
                section.style.display = 'none';
            }
        });
    }

    /**
    * Show all main content sections
    */
    function showAllSections()
    {
        const sections = ['home', 'uber', 'services', 'webshop', 'contact'];
        sections.forEach(sectionId =>
        {
            const section = document.getElementById(sectionId);
            if (section)
            {
                section.style.display = 'block';
            }
        });
    }

    /**
    * Scroll to section with header offset (optimized for mobile)
    * @param {string} sectionId - The ID of the section to scroll to
    */
    function scrollToSectionWithOffset(sectionId)
    {
        // Cache window.innerWidth once and batch layout reads
        const isMobile = window.innerWidth <= 768;

        requestAnimationFrame(() =>
        {
            const section = document.getElementById(sectionId);
            if (section)
            {
                // Batch layout reads
                const headerHeight = getHeaderHeight();
                const targetPosition = section.offsetTop - headerHeight - 20;

                // Use smooth scrolling (same for mobile and desktop)
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });

                // Add visual feedback for section navigation
                requestAnimationFrame(() =>
                {
                    section.style.outline = '2px solid var(--primary-color)';
                    section.style.outlineOffset = '2px';
                    setTimeout(() =>
                    {
                        section.style.outline = '';
                        section.style.outlineOffset = '';
                    }, 2000);
                });
            }
        });
    }

    /**
    * Scroll to contact section
    */
    function scrollToContact()
    {
        // Use requestAnimationFrame to batch layout reads
        requestAnimationFrame(() =>
        {
            const contactSection = document.getElementById('contact');
            const header = document.querySelector('header');

            if (contactSection && header)
            {
                // Batch layout reads
                const headerHeight = header.offsetHeight;
                const targetPosition = contactSection.offsetTop - headerHeight - 20;

                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });

                // Update navigation highlighting after scroll completes
                setTimeout(updateNavigationHighlight, 500);
            }
        });
    }

    /**
    * Check if dashboard is currently open
    * @returns {boolean} True if dashboard is open
    */
    function isDashboardOpen()
    {
        const dashboardSection = document.getElementById('dashboard');
        return dashboardSection && dashboardSection.style.display === 'block';
    }

    /**
    * Hide the dashboard
    */
    function hideDashboard()
    {
        const dashboardSection = document.getElementById('dashboard');
        if (dashboardSection)
        {
            dashboardSection.style.display = 'none';
        }
        isDashboardVisible = false;

        // Clear dashboard state tracking
        localStorage.removeItem('wasOnDashboard');

        // Update menu highlighting after hiding dashboard
        setTimeout(() =>
        {
            if (window.updateMenuHighlighting)
            {
                window.updateMenuHighlighting();
            }
        }, 100);
    }

    /**
    * Show the dashboard
    */
    function showDashboard()
    {
        // Hide all sections except dashboard
        const sections = ['home', 'uber', 'services', 'webshop', 'contact'];
        sections.forEach(sectionId =>
        {
            const section = document.getElementById(sectionId);
            if (section)
            {
                section.style.display = 'none';
            }
        });

        const dashboardSection = document.getElementById('dashboard');
        if (dashboardSection)
        {
            dashboardSection.style.display = 'block';
            setTimeout(() =>
            {
                scrollToSectionWithOffset('dashboard');
            }, 50);
        }
        isDashboardVisible = true;

        // Update menu highlighting after showing dashboard
        setTimeout(() =>
        {
            if (window.updateMenuHighlighting)
            {
                window.updateMenuHighlighting();
            }
        }, 100);
    }

    /**
    * Check if navigation items overflow and switch to dropdown menu
    * Optimized to batch all layout reads before writes to avoid forced reflows
    */
    function checkNavigationOverflow()
    {
        const nav = document.querySelector('nav');
        const navLinks = document.querySelector('.nav-links');
        const mobileToggle = document.querySelector('.mobile-menu-toggle');

        if (!nav || !navLinks || !mobileToggle) return;

        // Use requestAnimationFrame to batch layout reads
        requestAnimationFrame(() =>
        {
            // Temporarily show nav-links to measure (write)
            const originalDisplay = navLinks.style.display;
            const originalComputedDisplay = window.getComputedStyle(navLinks).display;
            navLinks.style.display = 'flex';

            // Batch all layout reads together (after display change, browser will recalculate once)
            requestAnimationFrame(() =>
            {
                // Read all layout properties in one batch
                const logo = nav.querySelector('.logo');
                const logoWidth = logo ? logo.offsetWidth : 0;

                const headerButtons = nav.querySelectorAll('button:not(.mobile-menu-toggle)');
                let headerButtonsWidth = 0;
                headerButtons.forEach(button =>
                {
                    headerButtonsWidth += button.offsetWidth;
                });

                const navWidth = nav.offsetWidth;
                const navLinksWidth = navLinks.scrollWidth;
                const toggleWidth = mobileToggle.offsetWidth;
                const padding = 40;
                const availableWidth = navWidth - logoWidth - headerButtonsWidth - toggleWidth - padding;

                // Restore original display and apply final styles in one batch (write)
                if (navLinksWidth > availableWidth)
                {
                    navLinks.style.display = 'none';
                    mobileToggle.style.display = 'block';
                }
                else
                {
                    navLinks.style.display = originalDisplay || originalComputedDisplay;
                    mobileToggle.style.display = 'none';
                }
            });
        });
    }

    /**
    * Initialize mobile menu functionality
    */
    function initializeMobileMenu()
    {
        const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
        const mobileNav = document.querySelector('.mobile-nav');

        if (mobileMenuToggle && mobileNav)
        {
            // Enhanced mobile menu toggle with haptic feedback
            mobileMenuToggle.addEventListener('click', (e) =>
            {
                e.stopPropagation();
                e.preventDefault();

                const isActive = mobileNav.classList.contains('active');

                if (isActive)
                {
                    // Close menu
                    mobileNav.classList.remove('active');
                    mobileNav.style.display = 'none';
                    mobileNav.style.visibility = 'hidden';
                    mobileNav.style.opacity = '0';
                    mobileNav.style.pointerEvents = 'none';
                    mobileMenuToggle.setAttribute('aria-expanded', 'false');
                    mobileNav.setAttribute('aria-hidden', 'true');
                    document.body.style.overflow = '';
                    // Remove body class for button font size reduction (fallback for browsers without :has() support)
                    if (window.innerWidth < 600)
                    {
                        document.body.classList.remove('mobile-nav-active');
                    }
                } else
                {
                    // Open menu
                    mobileNav.classList.add('active');
                    // Add body class for button font size reduction when screen is under 600px (fallback for browsers without :has() support)
                    if (window.innerWidth < 600)
                    {
                        document.body.classList.add('mobile-nav-active');
                    }
                    // Force display with inline styles as fallback
                    mobileNav.style.display = 'block';
                    mobileNav.style.visibility = 'visible';
                    mobileNav.style.opacity = '1';
                    mobileNav.style.pointerEvents = 'auto';
                    mobileNav.style.position = 'fixed';
                    mobileNav.style.top = '56px';
                    mobileNav.style.left = '0';
                    mobileNav.style.right = '0';
                    mobileNav.style.width = '100%';
                    mobileNav.style.zIndex = '99999';
                    mobileNav.style.background = 'rgb(255, 255, 255)';
                    mobileMenuToggle.setAttribute('aria-expanded', 'true');
                    mobileNav.setAttribute('aria-hidden', 'false');
                    document.body.style.overflow = 'hidden';
                }

                // Force menu content to be visible
                const menuUl = mobileNav.querySelector('ul');
                const menuLinks = mobileNav.querySelectorAll('a');

                if (menuUl)
                {
                    menuUl.style.display = 'block';
                    menuUl.style.visibility = 'visible';
                    menuUl.style.opacity = '1';
                    menuUl.style.position = 'relative';
                    menuUl.style.zIndex = '99999';
                }
                menuLinks.forEach(link =>
                {
                    link.style.display = 'block';
                    link.style.visibility = 'visible';
                    link.style.opacity = '1';
                });

                // Haptic feedback for supported devices
                if ('vibrate' in navigator)
                {
                    navigator.vibrate(50);
                }
            });

            // Close mobile menu when clicking on links
            const mobileNavLinks = mobileNav.querySelectorAll('a');
            mobileNavLinks.forEach(link =>
            {
                link.addEventListener('click', () =>
                {
                    mobileNav.classList.remove('active');
                    // Force hide with inline styles
                    mobileNav.style.display = 'none';
                    mobileNav.style.visibility = 'hidden';
                    mobileNav.style.opacity = '0';
                    mobileNav.style.pointerEvents = 'none';
                    mobileMenuToggle.setAttribute('aria-expanded', 'false');
                    mobileNav.setAttribute('aria-hidden', 'true');
                    document.body.style.overflow = '';
                    // Remove body class for button font size reduction (fallback for browsers without :has() support)
                    if (window.innerWidth < 600)
                    {
                        document.body.classList.remove('mobile-nav-active');
                    }
                });
            });

            // Close mobile menu when clicking outside
            document.addEventListener('click', (e) =>
            {
                if (!mobileNav.contains(e.target) && !mobileMenuToggle.contains(e.target))
                {
                    if (mobileNav.classList.contains('active'))
                    {
                        mobileNav.classList.remove('active');
                        // Force hide with inline styles
                        mobileNav.style.display = 'none';
                        mobileNav.style.visibility = 'hidden';
                        mobileNav.style.opacity = '0';
                        mobileNav.style.pointerEvents = 'none';
                        mobileMenuToggle.setAttribute('aria-expanded', 'false');
                        mobileNav.setAttribute('aria-hidden', 'true');
                        document.body.style.overflow = '';
                        // Remove body class for button font size reduction (fallback for browsers without :has() support)
                        if (window.innerWidth < 600)
                        {
                            document.body.classList.remove('mobile-nav-active');
                        }
                    }
                }
            });

            // Close mobile menu on escape key
            document.addEventListener('keydown', (e) =>
            {
                if (e.key === 'Escape' && mobileNav.classList.contains('active'))
                {
                    closeMobileMenu();
                }
            });

            // Update mobile menu based on device type
            if (window.MobileDetection)
            {
                // Initial update
                updateMobileMenuVisibility();

                // Listen for device type changes
                window.addEventListener('resize', () =>
                {
                    setTimeout(updateMobileMenuVisibility, 100);
                });
            }
        }

        // Check for navigation overflow on load and resize
        checkNavigationOverflow();
        window.addEventListener('resize', checkNavigationOverflow);
    }

    /**
    * Open mobile menu with enhanced animation
    */
    function openMobileMenu()
    {
        const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
        const mobileNav = document.querySelector('.mobile-nav');

        if (mobileMenuToggle && mobileNav)
        {
            mobileNav.classList.add('active');
            mobileMenuToggle.classList.add('active');
            document.body.style.overflow = 'hidden';

            // Add haptic feedback if available
            if (navigator.vibrate)
            {
                navigator.vibrate(50);
            }
        }
    }

    /**
    * Close mobile menu with enhanced animation
    */
    function closeMobileMenu()
    {
        const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
        const mobileNav = document.querySelector('.mobile-nav');

        if (mobileMenuToggle && mobileNav)
        {
            mobileNav.classList.remove('active');
            mobileMenuToggle.classList.remove('active');
            // Force hide with inline styles
            mobileNav.style.display = 'none';
            mobileNav.style.visibility = 'hidden';
            mobileNav.style.opacity = '0';
            mobileNav.style.pointerEvents = 'none';
            mobileMenuToggle.setAttribute('aria-expanded', 'false');
            mobileNav.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        }
    }

    /**
    * Update mobile menu visibility based on device type
    */
    function updateMobileMenuVisibility()
    {
        if (!window.MobileDetection)
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('MobileDetection not available, skipping mobile menu update');
            return;
        }

        const navLinks = document.querySelector('.nav-links');
        const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
        const mobileNav = document.querySelector('.mobile-nav');

        if (!navLinks || !mobileMenuToggle || !mobileNav)
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Navigation elements not found, skipping mobile menu update');
            return;
        }

        const deviceInfo = window.MobileDetection.getDeviceInfo();

        if (deviceInfo.isMobile)
        {
            // Hide desktop navigation, show mobile toggle
            navLinks.style.display = 'none';
            mobileMenuToggle.style.display = 'flex';
            // Don't set mobileNav.style.display - let CSS handle it

            // Logo margin is handled by CSS (60px to make room for menu toggle)
            // No need to set it via JavaScript as CSS handles it

            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('📱 Mobile menu activated for mobile device');
        }
        else if (deviceInfo.isTablet)
        {
            // For tablets, use desktop navigation but ensure it fits
            navLinks.style.display = 'flex';
            mobileMenuToggle.style.display = 'none';
            // Don't set inline display style - let CSS handle it
            mobileNav.classList.remove('active');

            // Reduce gap for tablets to ensure navigation fits
            navLinks.style.gap = '1rem';

            // Reset logo margin for tablets
            const logo = document.querySelector('.logo');
            if (logo)
            {
                logo.style.marginLeft = '0';
            }

            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('📱 Tablet navigation activated');
        }
        else
        {
            // Show desktop navigation, hide mobile toggle
            navLinks.style.display = 'flex';
            mobileMenuToggle.style.display = 'none';
            // Don't set inline display style - let CSS handle it
            mobileNav.classList.remove('active');

            // Reset gap for desktop
            navLinks.style.gap = '2rem';

            // Reset logo margin for desktop
            const logo = document.querySelector('.logo');
            if (logo)
            {
                logo.style.marginLeft = '0';
            }

            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🖥️ Desktop navigation activated');
        }
    }

    /**
    * Add touch support for better mobile experience
    */
    function addTouchSupport()
    {
        // Add touch class for CSS styling
        const buttons = document.querySelectorAll('button, .btn, .add-to-cart, .option-item');
        buttons.forEach(button =>
        {
            button.addEventListener('touchstart', function ()
            {
                this.classList.add('touch-active');
            });

            button.addEventListener('touchend', function ()
            {
                setTimeout(() =>
                {
                    this.classList.remove('touch-active');
                }, 150);
            });
        });

        // Prevent double-tap zoom on buttons
        let lastTouchEnd = 0;
        document.addEventListener('touchend', function (event)
        {
            // Don't handle touch events when a modal dialog is open (unless inside the modal)
            if (window.UIUtils && window.UIUtils.isModalOpen && window.UIUtils.isModalOpen())
            {
                const modalIds = ['checkoutPage', 'bankTransferModal', 'cartModal', 'invoiceModal', 'paypalSummaryModal'];
                const isInsideModal = modalIds.some(id =>
                {
                    const modal = document.getElementById(id);
                    return modal && modal.contains(event.target);
                });

                if (!isInsideModal)
                {
                    return; // Ignore touches outside modals
                }
            }

            const now = (new Date()).getTime();
            if (now - lastTouchEnd <= 300)
            {
                event.preventDefault();
            }
            lastTouchEnd = now;
        }, false);
    }

    /**
    * Initialize parallax background effect with mobile optimization
    */
    function initializeParallax()
    {
        let ticking = false;
        let lastScrollY = 0;

        function updateParallax()
        {
            const scrolled = window.pageYOffset;
            const isMobile = window.innerWidth <= 768;

            // Reduce parallax effect on mobile for better performance
            if (isMobile)
            {
                // Only update every few pixels on mobile
                if (Math.abs(scrolled - lastScrollY) < 5)
                {
                    ticking = false;
                    return;
                }
                lastScrollY = scrolled;
            }

            const parallaxSpeed = isMobile ? 0.2 : 0.7;
            const yPos = -(scrolled * parallaxSpeed);

            // Use background position instead of transform to avoid breaking sticky positioning
            // Transform on body breaks sticky header positioning on mobile
            if (isMobile)
            {
                // Use background position instead of transform to preserve sticky positioning
                document.body.style.backgroundPosition = `center ${yPos}px`;
                // Reset any transform that might have been applied
                document.body.style.transform = '';
            }
            else
            {
                document.body.style.backgroundPosition = `center ${yPos}px`;
            }

            ticking = false;
        }

        function requestParallaxUpdate()
        {
            if (!ticking)
            {
                requestAnimationFrame(updateParallax);
                ticking = true;
            }
        }

        // Use passive listeners for better performance
        window.addEventListener('scroll', requestParallaxUpdate, { passive: true });

        // Reset parallax on orientation change
        window.addEventListener('orientationchange', () =>
        {
            setTimeout(() =>
            {
                lastScrollY = window.pageYOffset;
                updateParallax();
            }, 100);
        });
    }

    /**
    * Initialize scroll animations for sections - FADE EFFECTS REMOVED
    * Elements are now always visible without opacity transitions
    */
    function initializeScrollAnimations()
    {
        // Create intersection observer for scroll animations
        const observerOptions = {
            root: null,
            rootMargin: '0px 0px -50px 0px',
            threshold: [0.1, 0.5, 0.9] // Multiple thresholds for smoother transitions
        };

        let observer = new IntersectionObserver((entries) =>
        {
            entries.forEach(entry =>
            {
                const element = entry.target;
                const visibilityRatio = entry.intersectionRatio;

                // Remove all animation classes - no fade effects
                element.classList.remove('animate-in', 'animate-partial', 'animate-out', 'scroll-fade-in');

                // Elements are always visible - no opacity changes
                // No animation classes added
            });
        }, observerOptions);

        // Observe all sections
        const sections = document.querySelectorAll('.section');
        sections.forEach(section =>
        {
            // Sections are always visible - no fade effects
            observer.observe(section);
        });

        // Disconnect observer when page is unloaded for memory optimization
        const cleanupObserver = () =>
        {
            if (observer)
            {
                observer.disconnect();
                observer = null;
            }
        };

        // Store cleanup function globally to avoid duplicate listeners
        if (!window._scrollObserverCleanup)
        {
            window._scrollObserverCleanup = cleanupObserver;

            // Use pagehide as primary cleanup event (more reliable than beforeunload)
            window.addEventListener('pagehide', cleanupObserver, { passive: true });

            // Fallback to beforeunload for older browsers
            window.addEventListener('beforeunload', cleanupObserver, { passive: true });

            // Also cleanup when page becomes hidden
            document.addEventListener('visibilitychange', () =>
            {
                if (document.hidden)
                {
                    cleanupObserver();
                }
            }, { passive: true });
        }

        console.log('⟳ Scroll animations initialized for', sections.length, 'sections');
    }

    /**
    * Enhanced smooth scrolling with easing and visual feedback
    */
    function addEnhancedScrolling()
    {
        // Add easing to programmatic scrolling
        function easeInOutCubic(t)
        {
            return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        }

        // Override default scrollToSectionWithOffset with enhanced behavior
        const originalScrollToSection = scrollToSectionWithOffset;
        if (originalScrollToSection)
        {
            // Create a global reference to the original function
            window.scrollToSectionWithOffset = function (sectionId)
            {
                // Use requestAnimationFrame to batch layout reads
                requestAnimationFrame(() =>
                {
                    const section = document.getElementById(sectionId);
                    const header = document.querySelector('header');

                    if (section && header)
                    {
                        // Batch all layout reads
                        const headerHeight = header.offsetHeight;
                        const targetPosition = section.offsetTop - headerHeight - 20;
                        const startPosition = window.pageYOffset || window.scrollY || 0;
                        const distance = targetPosition - startPosition;
                        const duration = Math.min(800, Math.abs(distance) / 2); // Dynamic duration

                        let start = null;

                        function animation(currentTime)
                        {
                            if (start === null) start = currentTime;
                            const timeElapsed = currentTime - start;
                            const progress = Math.min(timeElapsed / duration, 1);

                            const easenProgress = easeInOutCubic(progress);
                            window.scrollTo(0, startPosition + distance * easenProgress);

                            if (progress < 1)
                            {
                                requestAnimationFrame(animation);
                            }
                        }

                        requestAnimationFrame(animation);

                        // Add visual feedback for section navigation
                        setTimeout(() =>
                        {
                            requestAnimationFrame(() =>
                            {
                                section.style.outline = '2px solid var(--primary-color)';
                                section.style.outlineOffset = '2px';
                                setTimeout(() =>
                                {
                                    section.style.outline = '';
                                    section.style.outlineOffset = '';
                                }, 2000);
                            });
                        }, duration / 2);

                        // Update navigation highlighting after scroll completes
                        setTimeout(updateNavigationHighlight, duration + 100);
                    }
                });
            };
        }
    }

    /**
    * Initialize all UI utilities and components
    * 
    * This function initializes all UI-related components and utilities for the application.
    * It sets up mobile menu functionality, touch support, parallax effects, and scroll animations.
    * The function also ensures proper initial state of sections and adds necessary CSS animations for notifications.
    * 
    * Features:
    * - Section visibility management
    * - Mobile menu initialization
    * - Touch support for mobile devices
    * - Parallax background effects
    * - Scroll animations for sections
    * - CSS animation definitions
    * - Dashboard state management
    * 
    * @returns {void} No return value
    */
    /**
     * Update CSS variable for header height to position modals correctly
     */
    function updateModalHeaderSpacing()
    {
        const header = document.querySelector('header');
        if (header)
        {
            const headerHeight = header.offsetHeight;
            document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        }
    }

    function initializeUIUtils()
    {
        // Ensure all sections are visible by default FIRST
        showAllSections();

        // Update modal header spacing based on actual header height
        // Use requestAnimationFrame to ensure header is fully rendered
        requestAnimationFrame(() =>
        {
            updateModalHeaderSpacing();
            // Also update after a short delay to catch any dynamic content
            setTimeout(updateModalHeaderSpacing, 100);
        });

        // Update on window resize
        let resizeTimeout;
        window.addEventListener('resize', () =>
        {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() =>
            {
                updateModalHeaderSpacing();
            }, 100);
        });

        // Start monitoring modal state to adjust header elements z-index
        startModalStateMonitoring();

        // Initialize all UI components in proper order
        initializeMobileMenu();
        addTouchSupport();
        initializeParallax();
        initializeScrollAnimations();

        // Hide dashboard by default to prevent conflicts
        const dashboardSection = document.getElementById('dashboard');
        if (dashboardSection)
        {
            dashboardSection.style.display = 'none';
        }

        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('✅ All sections initialized and visible');

        // Add CSS animations for notifications and touch interactions
        const style = document.createElement('style');
        style.textContent = `
        @keyframes slideInRight {
            from {
                transform: translateX(100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }

        @keyframes slideOutRight {
            from {
                transform: translateX(0);
                opacity: 1;
            }
            to {
                transform: translateX(100%);
                opacity: 0;
            }
        }

        .touch-active {
            opacity: 0.7;
            transform: scale(0.95);
        }
        `;
        document.head.appendChild(style);
    }

    /**
    * Show privacy policy modal
    */
    function showPrivacyPolicy()
    {
        // Cache window.innerWidth once to avoid forced reflow
        const isMobile = window.innerWidth <= 768;

        // Remove any existing privacy modal
        const existingModal = document.getElementById('privacyModal');
        if (existingModal)
        {
            existingModal.remove();
        }

        const modalHTML = `
        <div id="privacyModal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: ${isMobile ? '#000000' : 'rgba(0, 0, 0, 0.8)'}; z-index: ${isMobile ? '10001' : '11000'}; display: flex; align-items: center; justify-content: center; padding: 2rem;">
        <div style="background: white; border-radius: 15px; padding: 0; max-width: 900px; width: 100%; max-height: 90vh; overflow: hidden; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column;">
        <div style="background: #667eea; color: white; padding: 1.5rem 2rem; border-radius: 15px 15px 0 0; display: flex; justify-content: space-between; align-items: center;">
        <h2 style="margin: 0; font-size: 1.5rem; font-weight: 600;">🔒 Datenschutzerklärung</h2>
        </div>
        <div style="padding: 2rem; overflow-y: auto; flex: 1;">
        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">1. Datenschutz auf einen Blick</h3>

        <h4 style="color: #555; margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">🔍 Allgemeine Hinweise</h4>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0; text-align: justify;">Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.</p>

        <h4 style="color: #555; margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">📊 Datenerfassung auf dieser Website</h4>

        <h5 style="color: #666; margin: 1rem 0 0.25rem 0; font-weight: 600;">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h5>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.</p>

        <h5 style="color: #666; margin: 1rem 0 0.25rem 0; font-weight: 600;">Wie erfassen wir Ihre Daten?</h5>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben. Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere ITSysteme erfasst. Das sind vor allem technische Daten (z. B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt automatisch, sobald Sie diese Website betreten.</p>

        <h5 style="color: #666; margin: 1rem 0 0.25rem 0; font-weight: 600;">Wofür nutzen wir Ihre Daten?</h5>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.</p>

        <h5 style="color: #666; margin: 1rem 0 0.25rem 0; font-weight: 600;">Welche Rechte haben Sie bezüglich Ihrer Daten?</h5>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an uns wenden.</p>

        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">2. Hosting</h3>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">3. Allgemeine Hinweise und Pflichtinformationen</h3>

        <h4 style="color: #555; margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">🛡️ Datenschutz</h4>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.</p>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Wenn Sie diese Website benutzen, werden verschiedene personenbezogene Daten erhoben. Personenbezogene Daten sind Daten, mit denen Sie persönlich identifiziert werden können. Die vorliegende Datenschutzerklärung erläutert, welche Daten wir erheben und wofür wir sie nutzen. Sie erläutert auch, wie und zu welchem Zweck das geschieht.</p>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Wir weisen darauf hin, dass die Datenübertragung im Internet (z. B. bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich.</p>

        <h4 style="color: #555; margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">📋 Hinweis zur verantwortlichen Stelle</h4>
        <div style="background: #f8f9fa; padding: 1rem; border-radius: 8px; margin: 1rem 0; border-left: 4px solid #667eea;">
        <p style="margin: 0.25rem 0; color: #333;"><strong style="color: #667eea;">Verantwortlicher:</strong></p>
        <p style="margin: 0.25rem 0; color: #333;"><strong>Velizar Kolev</strong></p>
        <p style="margin: 0.25rem 0; color: #333;">Edith-Stein-Weg 9</p>
        <p style="margin: 0.25rem 0; color: #333;">64285 Darmstadt</p>
        <p style="margin: 0.25rem 0; color: #333;">Telefon: +49 (0) 151 145 19347</p>
        <p style="margin: 0.25rem 0; color: #333;">E-Mail: info@d-luft.de</p>
        </div>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten (z. B. Namen, E-Mail-Adressen o. Ä.) entscheidet.</p>

        <p style="color: #555; line-height: 1.7; margin: 1rem 0;"><strong style="color: #333;">Weitere wichtige Informationen zu Ihren Rechten:</strong></p>
        <ul style="color: #555; line-height: 1.7; margin: 1rem 0; padding-left: 1.5rem;">
        <li>Auskunft über Ihre gespeicherten Daten</li>
        <li>Berichtigung oder Löschung Ihrer Daten</di>
        <li>Widerruf Ihrer Einwilligung</li>
        <li>Beschwerderecht bei der Aufsichtsbehörde</li>
        <li>Recht auf Datenübertragbarkeit</li>
        <li>Einschränkung der Datenverarbeitung</li>
        </ul>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">4. Datenerfassung auf dieser Website</h3>

        <h4 style="color: #555; margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">🍪 Cookies</h4>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine Textdateien und richten auf Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.</p>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Session-Cookies werden nach Ende Ihres Besuchs automatisch gelöscht. Permanente Cookies bleiben auf Ihrem Endgerät gespeichert, bis Sie diese selbst löschen oder eine automatische Löschung durch Ihren Webbrowser erfolgt.</p>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden und Cookies nur im Einzelfall erlauben, die Annahme von Cookies für bestimmte Fälle oder generell ausschließen sowie das automatische Löschen der cookies beim Schließen des Browsers aktivieren.</p>

        <h4 style="color: #555; margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">📝 Kontaktformular und E-Mail-Anfragen</h4>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Wenn Sie uns per Kontaktformular oder E-Mail Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.</p>
        <p style="color: #555; line-height: 1.7; margin: 1rem 0;">Die von Ihnen eingegebenen Daten verbleiben bei uns, bis Sie uns zur Lösung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage).</p>
        </div>


        <div style="background: #fff3cd; padding: 1rem; border-radius: 8px; border-left: 4px solid #ffc107; margin: 2rem 0;">
        <p style="margin: 0; color: #856404;"><strong>Quelle:</strong> <a href="https://www.e-recht24.de" target="_blank" rel="noopener noreferrer" style="color: #667eea; text-decoration: none;">https://www.e-recht24.de</a></p>
        </div>
        </div>
        </div>
        </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);
    }

    /**
    * Show Impressum modal
    */
    function showImpressum()
    {
        // Cache window.innerWidth once to avoid forced reflow
        const isMobile = window.innerWidth <= 768;

        // Remove any existing Impressum modal
        const existingModal = document.getElementById('impressumModal');
        if (existingModal)
        {
            existingModal.remove();
        }

        const modalHTML = `
        <div id="impressumModal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: ${isMobile ? '#000000' : 'rgba(0, 0, 0, 0.8)'}; z-index: ${isMobile ? '10001' : '11000'}; display: flex; align-items: center; justify-content: center; padding: 2rem;">
        <div style="background: white; border-radius: 15px; padding: 0; max-width: 900px; width: 100%; max-height: 90vh; overflow: hidden; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column;">
        <div style="background: #666eee; color: white; padding: 1.5rem 2rem; border-radius: 15px 15px 0 0; display: flex; justify-content: space-between; align-items: center;">
        <h2 style="margin: 0; font-size: 1.5rem; font-weight: 600;">📋 Impressum</h2>
        </div>
        <div style="padding: 2rem; overflow-y: auto; flex: 1;">
        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">📋 Verantwortlich</h3>
        <div style="background: #f8f9fa; padding: 1rem; border-radius: 8px; border-left: 4px solid #666eee;">
        <p style="margin: 0.25rem 0; color: #333;"><strong style="color: #666eea;">BIGISOFT</strong></p>
        <p style="margin: 0.25rem 0; color: #333;">Velizar Kolev</p>
        <p style="margin: 0.25rem 0; color: #333;">Edith-Stein-Weg 9</p>
        <p style="margin: 0.25rem 0; color: #333;">64285 Darmstadt</p>
        <p style="margin: 0.25rem 0; color: #333;">DEUTSCHLAND</p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">📞 Kontakt</h3>
        <div style="background: #d3d3d3; padding: 1rem; border-radius: 8px; border-left: 4px solid #666eee;">
        <p style="margin: 0.25rem 0; color: #333;"><strong>Velizar Kolev</strong></p>
        <p style="margin: 0.25rem 0; color: #666; font-style: italic;">(Anschrift wie oben)</p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">💰 Umsatzsteuer-ID</h3>
        <div style="background: #d3d3d3; padding: 1rem; border-radius: 8px; border-left: 4px solid #666eee;">
        <p style="margin: 0.25rem 0; color: #333; font-family: monospace; font-weight: 500; font-size: 1.25rem;">DE316952412</p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">⚖️ Informationen zur Online-Streitbeilegung</h3>
        <div style="background: #d3d3d3; padding: 1rem; border-radius: 8px; border-left: 4px solid #666eee;">
        <p style="margin: 1rem 0; color: #333; line-height: 1.7;">Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit.</p>
        <p style="margin: 1rem 0; color: #333; line-height: 1.7;">Diese Plattform finden Sie unter folgendem Link: <a href="https://ec.europa.eu/consumers/odr/main/?event=main.home2.show#" target="_blank" rel="noopener noreferrer" style="color: #667eea; text-decoration: none;">https://ec.europa.eu/consumers/odr/main/?event=main.home2.show#</a></p>
        <p style="margin: 1rem 0; color: #333; line-height: 1.7;">Verbraucher können diese Plattform nutzen, um ihre Streitigkeiten aus Online-Verträgen beizulegen.</p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">📢 Hinweis gemäß § 36 VSBG</h3>
        <div style="background: #d3d3d3; padding: 1rem; border-radius: 8px; border-left: 4px solid #666eee;">
        <p style="margin: 1rem 0; color: #333; line-height: 1.7;">Wir werden nicht an alternativen Streitschlichtungsverfahren im Sinne des § 36 VSBG teilnehmen. Die Nutzung einer alternativen Schlichtungsstelle stellt keine zwingende Voraussetzung für das Anrufen zuständiger ordentlicher Gerichte dar.</p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">©️ Urheberrecht und Bildnachweise</h3>
        <div style="background: #d3d3d3; padding: 1rem; border-radius: 8px; border-left: 4px solid #666eee;">
        <p style="margin: 1rem 0; color: #333; line-height: 1.7;">Die Inhalte von www.d-luft.de sind – soweit nicht abweichend angegeben – urheberrechtlich geschützt.</p>
        <p style="margin: 1rem 0; color: #333; line-height: 1.7;">Verwendete Fotografien sind ggf. mit Bildnachweisen gekennzeichnet oder unten aufgeführt, soweit sie nicht selbst angefertigt wurden.</p>
        <p style="margin: 1rem 0; color: #333; line-height: 1.7;">Die Verwendung von Fotografien auf Drittseiten ist nur im Rahmen der jeweiligen Lizenz der Urheber möglich.</p>
        </div>
        </div>
        </div>
        </div>
        </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);
    }

    /**
    * Show Widerruf modal - redirects to AGB and scrolls to withdrawal section
    */
    function showWiderruf()
    {
        // Open the AGB dialog first
        showAGB();

        // Wait for the AGB modal to be rendered, then scroll to the withdrawal section
        setTimeout(() =>
        {
            const widerrufSection = document.getElementById('widerruf-section');
            if (widerrufSection)
            {
                // Scroll the section into view within the modal
                widerrufSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                // Highlight the section briefly to draw attention
                const originalBackground = widerrufSection.style.background;
                widerrufSection.style.background = 'rgba(220, 53, 69, 0.1)';
                widerrufSection.style.transition = 'background 0.3s ease';

                setTimeout(() =>
                {
                    widerrufSection.style.background = originalBackground;
                }, 2000);
            }
        }, 100);
    }

    /**
    * Show Barrierefreiheit modal
    */
    function showBarrierefreiheit()
    {
        // Open the AGB dialog first
        showAGB();

        // Wait for the AGB modal to be rendered, then scroll to the Barrierefreiheit section
        setTimeout(() =>
        {
            const barrierefreiheitSection = document.getElementById('barrierefreiheit-section');
            if (barrierefreiheitSection)
            {
                // Scroll the section into view within the modal
                barrierefreiheitSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                // Highlight the section briefly to draw attention
                const originalBackground = barrierefreiheitSection.style.background;
                barrierefreiheitSection.style.background = 'rgba(220, 53, 69, 0.1)';
                barrierefreiheitSection.style.transition = 'background 0.3s ease';

                setTimeout(() =>
                {
                    barrierefreiheitSection.style.background = originalBackground;
                }, 2000);
            }
        }, 100);
    }

    /**
        const isMobile = window.innerWidth <= 768;

        // Remove any existing Barrierefreiheit modal
        const existingModal = document.getElementById('barrierefreiheitModal');
        if (existingModal)
        {
            existingModal.remove();
        }

        const modalHTML = `
        <div id="barrierefreiheitModal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: ${isMobile ? '#000000' : 'rgba(0, 0, 0, 0.8)'}; z-index: ${isMobile ? '10001' : '11000'}; display: flex; align-items: center; justify-content: center; padding: 2rem;">
        <div style="background: white; border-radius: 15px; padding: 0; max-width: 1000px; width: 100%; max-height: 90vh; overflow: hidden; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column;">
        <div style="background: black; color: white; padding: 1.5rem 2rem; border-radius: 15px 15px 0 0; display: flex; justify-content: space-between; align-items: center;">
        <h2 style="margin: 0; font-size: 1.5rem; font-weight: 600;">Erklärung zur Barrierefreiheit</h2>
        <button onclick="document.getElementById('barrierefreiheitModal').remove();" style="background: none; border: none; color: white; font-size: 1.5rem; cursor: pointer; padding: 0; line-height: 1;">&times;</button>
        </div>
        <div style="padding: 2rem; overflow-y: auto; flex: 1; line-height: 1.6;">
        
        <div style="margin-bottom: 2rem;">
        <p>Wir (BIGISOFT - Velizar Kolev) als Websitebetreiber sind bemüht, die Website in Einklang mit den einschlägigen Vorschriften zur Barrierefreiheit zu gestalten. Für uns gelten folgende Rechtsvorschriften:</p>
        <p>Landesgesetz zur Gleichstellung von Menschen mit Behinderungen (Landes-Behindertengleichstellungsgesetz - L-BGG) und L-BGG-Durchführungsverordnung</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">Website</h3>
        <p>Diese Erklärung zur Barrierefreiheit gilt für die Website https://www.d-luft.de/</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">Feedback und Kontaktangaben</h3>
        <p>Sind Ihnen Mängel beim barrierefreien Zugang zu Inhalten auf unserer Website aufgefallen oder haben Sie Anmerkungen sowie Fragen zum barrierefreien Zugang? Melden Sie sich gerne bei uns unter:</p>
        
        <div style="background: #f8f9fa; padding: 1.5rem; border-radius: 8px; border-left: 4px solid black; margin: 1rem 0;">
        <p style="margin: 0.25rem 0; color: #333;"><strong>BIGISOFT</strong></p>
        <p style="margin: 0.25rem 0; color: #333;">Velizar Kolev</p>
        <p style="margin: 0.25rem 0; color: #333;">Edith-Stein-Weg 9</p>
        <p style="margin: 0.25rem 0; color: #333;">64285 Darmstadt</p>
        <p style="margin: 0.25rem 0; color: #333;">Email: info@d-luft.de</p>
        <p style="margin: 0.25rem 0; color: #333;">Tel.: +49 (0) 151 145 19347</p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">Weitergehende Maßnahmen zur Verbesserung des barrierefreien Zugangs zu unserer Website</h3>
        <p>Wir bemühen uns stets um die Verbesserung der barrierefreien Zugangsmöglichkeiten zu unserer Website. Derzeit tun wir hierzu Folgendes:</p>
        <p>Helfen Sie uns, die Zugänglichkeit zu verbessern - D-Luft optimiert kontinuierlich Bereiche unserer Webseiten und Applikationen. Bitte lassen Sie uns wissen, wenn Sie während Ihrer Nutzung dieser auf Barrieren stoßen.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">Durchsetzungsverfahren</h3>
        <p>Sollten Sie der Ansicht sein, dass Sie durch eine nicht ausreichende barrierefreie Gestaltung unserer Website benachteiligt sind, können Sie sich an die zuständige Durchsetzungsstelle wenden. Diese erreichen Sie unter:</p>
        
        <div style="background: #f8f9fa; padding: 1.5rem; border-radius: 8px; border-left: 4px solid black; margin: 1rem 0;">
        <p style="margin: 0.25rem 0; color: #333;"><strong>Hessisches Ministerium für Arbeit, Integration, Jugend und Soziales</strong></p>
        <p style="margin: 0.25rem 0; color: #333;">Sonnenberger Straße 2/2a</p>
        <p style="margin: 0.25rem 0; color: #333;">65193 Wiesbaden</p>
        <p style="margin: 0.25rem 0; color: #333;">Telefon: +49 611 3219-0</p>
        <p style="margin: 0.25rem 0; color: #333;">E-Mail: poststelle@hsm.hessen.de</p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">Erstellung dieser Erklärung zur Barrierefreiheit</h3>
        <p>Diese Erklärung wurde am 24.01.2026 erstellt.</p>
        <p>Die Erklärung wurde zuletzt am 24.01.2026 überprüft.</p>
        </div>

        </div>
        </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);
    }

    /**
    * Show AGB modal
    */
    function showAGB()
    {
        // Cache window.innerWidth once to avoid forced reflow
        const isMobile = window.innerWidth <= 768;

        // Remove any existing AGB modal
        const existingModal = document.getElementById('agbModal');
        if (existingModal)
        {
            existingModal.remove();
        }

        const modalHTML = `
        <div id="agbModal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: ${isMobile ? '#000000' : 'rgba(0, 0, 0, 0.8)'}; z-index: ${isMobile ? '10001' : '11000'}; display: flex; align-items: center; justify-content: center; padding: 2rem;">
        <div style="background: white; border-radius: 15px; padding: 0; max-width: 1200px; width: 100%; max-height: 90vh; overflow: hidden; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column;">
        <div style="background: black; color: white; padding: 1.5rem 2rem; border-radius: 15px 15px 0 0; display: flex; justify-content: space-between; align-items: center;">
        <h2 style="margin: 0; font-size: 1.5rem; font-weight: 600;">Allgemeine Geschäftsbedingungen (AGB)</h2>
        <button onclick="document.getElementById('agbModal').remove();" style="background: none; border: none; color: white; font-size: 1.5rem; cursor: pointer; padding: 0; line-height: 1;">&times;</button>
        </div>
        <div style="padding: 2rem; overflow-y: auto; flex: 1; line-height: 1.6;">
        
        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">1. Geltung (Privatkunde)</h3>
        <p>Alle Lieferungen und Leistungen der BIGISOFT erfolgen ausschließlich auf Grundlage der nachfolgenden Allgemeinen Geschäftsbedingungen (AGB). Abweichende oder zusätzliche Allgemeine Geschäftsbedingungen unserer Kunden erkennen wir nicht an.</p>
        <p>Wir liefern zu diesen AGB ausschließlich innerhalb Deutschlands. Geschäftskunden mit einer gültigen Umsatzsteueridentifikationsnummer können eine Lieferung in Länder der EU unter Berechnung separater Versandkosten schriftlich anfragen.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">2. Angebot und Vertragsschluss</h3>
        <p>Sämtliche Angebote, Informationen und Inhalte dieser Website sind freibleibend und unverbindlich. Die Preise gelten nur bei Online-Bestellungen. Es steht uns zu, die Abgabe unserer Artikel auf eine handelsübliche Menge zu beschränken. Eine Lieferung an Großhändler ist ausgeschlossen. Wir behalten uns das Recht vor, die Belieferung von bestimmten Wiederverkäufern und Mitwettbewerbern auszuschließen.</p>
        <p>Die Angebote gelten solange der Vorrat reicht. Produktänderungen in Design, Farbe bzw. im technischen Bereich, die ausdrücklich der Qualitätsverbesserung des Produktes dienen, behalten wir uns ohne Vorankündigung vor. Im Internet veröffentlichte Bilder müssen nicht der Farbechtheit entsprechen bzw. können ähnlich sein.</p>
        <p>Durch Anklicken des Buttons "kaufen" geben Sie eine verbindliche Bestellung der im Warenkorb enthaltenen Waren ab. Nachdem uns Ihre Bestellung erreicht hat, erhalten Sie per E-Mail eine automatisch erstellte Eingangsbestätigung. Diese stellt keine verbindliche Annahme Ihrer Bestellung dar.</p>
        <p>Bei Bestellung mit der Zahlungsart Rechnung oder Sepa-Lastschrift erfolgt die Annahme Ihrer Bestellung innerhalb von zwei Tagen (Montag-Freitag) durch den Versand einer separaten Auftragsbestätigung oder durch Auslieferung der Ware.</p>
        <p>Bei Bestellungen mit der Zahlungsart PayPal oder Kreditkarte erfolgt die Annahme Ihrer Bestellung mit der Aufforderung zur Zahlung in der Kasse.</p>
        <p>Wir möchten darauf hinweisen, dass wir uns das Recht vorbehalten, den Kaufvertrag bei Irrtum, z.B. ein technischer Preisfehler etc., anzufechten.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">3. Lieferung</h3>
        <p>Der Paketversand erfolgt über den Paketdienst zu deren Lieferbedingungen.</p>
        <p>Die Lieferung erfolgt Montag bis Freitag, nur innerhalb EU.</p>
        <p>Wir behalten uns ausdrücklich vor, bestimmte Lieferadressen (z.B. Deportadressen, Postfächer, Packstationen & Postfilialen) aufgrund von Betrugsvorfällen zeitweise auszuschließen.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">4. Lieferzeit</h3>
        <p>Bei Bestellungen von Montag - Freitag bis 15.00 Uhr geht Ihre Bestellung noch am selben Tag zum Versand, soweit die Artikel vorrätig sind (Lagerware). Die Auslieferung erfolgt Montag bis Freitag (werktags). Bestellungen, welche per Rechnung gezahlt werden sollen, werden sofort geliefert, sobald Ihre Zahlung unserem Konto gutgeschrieben wurde und die Ware lagernd ist. Fällt der letzte Tag der Lieferzeit auf einen Sonntag oder auf einen am Lieferort staatlichen anerkannten allgemeinen Feiertag, so tritt an die Stelle eines solchen Tages der nächste Tag (Montag-Freitag).</p>
        </div>


        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">5. Versandkosten</h3>
        <p>Versandkostenfrei innerhalb der EU. Außerhalb der EU bitte Versandkosten vorher anfragen. Der Versand erfolgt per Deutsche Post (Briefversand).</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">6. Rechnungslegung, Fälligkeit und Verzug</h3>
        <p>Wir bieten sowohl für Geschäftskunden, Behörden und sonstige Institutionen, als auch für Privatkunden folgende Zahlungsmöglichkeiten an: SEPA-Lastschrift, Vorkasse, PayPal oder Kreditkarte.</p>
        <p>Generell gilt: Alle Rechnungen sind innerhalb von 14 Tagen nach Rechnungsdatum ohne jeden Abzug zur Zahlung fällig. Die Ware wird erst nach geldeingang verschickt. Alternativ dazu kann der Käufer der BIGISOFT ein SEPA-Basismandat erteilen. Der Einzug der Lastschrift erfolgt innerhalb von 7 Tagen nach Rechnungsdatum.</p>
        <p>Die Frist für die Vorabankündigung (Pre-Notification) wird auf 4 Tage verkürzt. Der Käufer sichert zu, für die Deckung des Kontos zu sorgen. Er haftet für alle Kosten, die aufgrund von Nichteinlösung oder Rückbuchung der Lastschrift entstehen, soweit diese von ihm zu vertreten sind. Die Belastung Ihres Kreditkarten- bzw. PayPal-Kontos erfolgt mit Abschluss der Bestellung. Wir behalten uns nach Prüfung (z.B. Bonitätsprüfung) vor, in begründeten Einzelfällen, nur unter der Verwendung der Zahlarten Vorkasse, PayPal bzw. Kreditkarte, zu liefern. Hierüber werden wir Sie rechtzeitig informieren.</p>
        <p>Wir versenden alle Rechnungen elektronisch im PDF-Format per E-Mail an die in Ihrem Kundenkonto hinterlegte E-Mail-Adresse, soweit keine abweichende Vereinbarung getroffen wurde. Ihre Zustimmung zum Empfang der Rechnung im PDF-Format gilt als Unternehmer i.S.d. § 27 Abs. 38 Umsatzsteuergesetz mit der Bestellung, im Übrigen mit verhergehender Annahme dieser AGB und Bestellung, als erteilt.</p>
        <p>Kommt es durch unsere Kunden zu unpünktlichen Zahlungen und es tritt Verzug auf, behalten wir uns vor, Dritte mit dem Einzug der Forderungen zu beauftragen. Die Kosten hierfür gehen dann zu Lasten des Kunden.</p>
        <div style="background: white; padding: 1rem; border: 1px solid black; margin-top: 1rem;">
        <p style="margin: 0; font-weight: 600;">BIGISOFT Bankverbindung:</p>
        <p style="margin: 0;">Informationen auf Anfrage verfügbar</p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">7. Eigentumsvorbehalt</h3>
        <p>Bei Verträgen mit Unternehmern behalten wir uns das Eigentum an der Kaufsache bis zum Eingang aller Zahlungen aus der Geschäftsverbindung mit dem Kunden vor. Der Unternehmer ist berechtigt, die Kaufsache im ordentlichen Geschäftsgang weiter zu verkaufen, er tritt uns aber bereits jetzt alle Forderungen in Höhe des Fakturabetrages (einschl. Umsatzsteuer sowie eventuelle Verzugszinsen) unserer Forderung ab, die ihm aus der Weiterveräußerung gegen seine Abnehmer oder Dritte erwachsen. Kommt der Unternehmer seiner Zahlungsverpflichtung aus den vereinnahmten Erlösen nicht nach, so können wir verlangen, dass der Unternehmer uns die abgetretene Forderung und deren Schuldner bekannt gibt, alle zum Einzug erforderlichen Angaben macht und den Schuldnern (Dritten) die Abtretung mitteilt. Bei Zugriff von Dritten, insbesondere Gerichtsvollzieher auf die Vorbehaltsware, wird der Auftraggeber (Kunde) auf unser Eigentum hinweisen und uns unverzüglich benachrichtigen, damit wir unsere Eigentumsrechte durchsetzen können.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">8. Rückgaberecht für Unternehmen nach §14 BGB</h3>
        <p>Wir gewähren Ihnen ein 14-tägiges kostenloses Rückgaberecht für paketversandfähige Waren.</p>
        <p>Bitte versenden Sie die Ware unbenutzt, originalverpackt und bruchsicher.</p>
        <p>Bei nicht ordnungsgemäßer Verpackung übernehmen wir für Transportschäden keine Haftung.</p>
        <p>Unfreie Sendungen werden grundsätzlich nicht angenommen.</p>
        <p>Nach Ablauf des 14-tägigen Rückgaberechts kann die Ware nur nach Absprache und unter Berechnung einer Bearbeitungsgebühr zurückgegeben werden. Diese Entscheidung obliegt der BIGISOFT.</p>
        
        <div style="background: white; border: 2px solid black; padding: 1.5rem; margin-top: 1.5rem;">
        <p style="line-height: 1.7; margin: 0 0 1rem 0;">
        Das Widerrufsrecht besteht gemäß § 312g BGB nicht bei Artikeln, die nicht vorgefertigt sind und für deren Herstellung eine individuelle Auswahl oder Bestimmung durch Sie maßgeblich ist oder die eindeutig auf Ihre persönlichen Bedürfnisse zugeschnitten sind, bei Waren, die schnell verderben können oder deren Verfallsdatum schnell überschritten würde, bei versiegelten Waren, die aus Gründen des Gesundheitsschutzes oder der Hygiene nicht zur Rückgabe geeignet sind, wenn ihre Versiegelung nach der Lieferung entfernt wurde oder bei Verträgen zur Lieferung von Ton- oder Videoaufnahmen oder Computersoftware in einer versiegelten Packung, wenn die Versiegelung nach der Lieferung entfernt wurde.
        </p>
        <p style="line-height: 1.7; margin: 0; font-weight: 600;">
        <strong>Dies betrifft insbesondere unsere digitalen Dienstleistungen wie D-Luft Performance Anpassungen und @Home MazdaEdit Lizenzen.</strong>
        </p>
        </div>
        </div>

        <div id="widerruf-section" style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">9. Widerrufsrecht für Verbraucher nach § 13 BGB</h3>
        
        <h4 style="margin: 1rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">9a. Widerrufsbelehrung</h4>
        <div style="background: white; border: 2px solid black; padding: 1.5rem; margin-bottom: 1rem;">
        <p style="line-height: 1.7; margin: 0 0 1rem 0;">
        Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.<br>
        Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag, an dem Sie oder ein von Ihnen benannter Dritter, der nicht der Beförderer ist, die letzte Ware in Besitz genommen haben bzw. hat.
        </p>
        <p style="line-height: 1.7; margin: 0 0 1rem 0;">
        Um Ihr Widerrufsrecht auszuüben, müssen Sie uns<br><br>
        <strong>BIGISOFT - Velizar Kolev</strong><br>
        Edith-Stein-Weg 9<br>
        64285 Darmstadt<br>
        E-Mail: info@d-luft.de<br>
        Tel: +49 (0) 151 145 19347<br><br>
        mittels einer eindeutigen Erklärung (z.B. ein mit der Post versandter Brief oder E-Mail) über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. Sie können dafür das beigefügte Muster-Widerrufsformular (unter Punkt 9c.) verwenden, das jedoch nicht vorgeschrieben ist.
        </p>
        <p style="line-height: 1.7; margin: 0;">
        Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist absenden.
        </p>
        </div>

        <h4 style="margin: 1.5rem 0 1rem 0; font-size: 1.1rem; font-weight: 600;">Folgen des Widerrufs</h4>
        <div style="background: white; padding: 1rem; border: 2px solid black;">
        <p style="margin: 0 0 1rem 0; line-height: 1.7;">
        Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, einschließlich der Lieferkosten (mit Ausnahme der zusätzlichen Kosten, die sich daraus ergeben, dass Sie eine andere Art der Lieferung als die von uns angebotene, günstigste Standardlieferung gewählt haben), unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses Vertrags bei uns eingegangen ist. Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart; in keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.
        </p>
        <p style="margin: 0; line-height: 1.7;">
        Sie müssen für einen etwaigen Wertverlust der Waren nur aufkommen, wenn dieser Wertverlust auf einen zur Prüfung der Beschaffenheit, Eigenschaften und Funktionsweise der Waren nicht notwendigen Umgang mit Ihnen zurückzuführen ist.
        </p>
        </div>

        <h4 style="margin: 1.5rem 0 1rem 0; font-size: 1.1rem; font-weight: 600;">9b. Hinweis auf Nichtbestehen des Widerrufsrechts</h4>
        <div style="background: white; border: 2px solid black; padding: 1.5rem;">
        <p style="line-height: 1.7; margin: 0 0 1rem 0;">
        Das Widerrufsrecht besteht gemäß § 312g BGB nicht bei Artikeln, die nicht vorgefertigt sind und für deren Herstellung eine individuelle Auswahl oder Bestimmung durch Sie maßgeblich ist oder die eindeutig auf Ihre persönlichen Bedürfnisse zugeschnitten sind, bei Waren, die schnell verderben können oder deren Verfallsdatum schnell überschritten würde, bei versiegelten Waren, die aus Gründen des Gesundheitsschutzes oder der Hygiene nicht zur Rückgabe geeignet sind, wenn ihre Versiegelung nach der Lieferung entfernt wurde oder bei Verträgen zur Lieferung von Ton- oder Videoaufnahmen oder Computersoftware in einer versiegelten Packung, wenn die Versiegelung nach der Lieferung entfernt wurde.
        </p>
        <p style="line-height: 1.7; margin: 0; font-weight: 600;">
        <strong>Dies betrifft insbesondere unsere digitalen Dienstleistungen wie D-Luft Performance Anpassungen und @Home MazdaEdit Lizenzen.</strong>
        </p>
        </div>

        <h4 style="margin: 1.5rem 0 1rem 0; font-size: 1.1rem; font-weight: 600;">9c. Muster-Widerrufsformular</h4>
        <div style="background: white; padding: 1rem; border: 2px solid black;">
        <p style="line-height: 1.7; margin: 0 0 1rem 0;">
        Sie können für den Widerruf unser Muster-Widerrufsformular verwenden:
        </p>
        <div style="background: white; padding: 1rem; border: 1px solid black; font-family: monospace; font-size: 0.9em;">
        <p style="margin: 0 0 1rem 0; font-weight: bold;">Muster-Widerrufsformular</p>
        <p style="margin: 0 0 1rem 0;">(Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte dieses Formular aus und senden Sie es an uns zurück.)</p>
        <p style="margin: 0 0 1rem 0;">
        An:<br>
        BIGISOFT - Velizar Kolev<br>
        Edith-Stein-Weg 9<br>
        64285 Darmstadt<br>
        Email: info@d-luft.de
        </p>
        <p style="margin: 0 0 1rem 0;">
        Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen Vertrag über den Kauf der folgenden Waren (*)/die Erbringung der folgenden Dienstleistung (*)
        </p>
        <p style="margin: 0 0 1rem 0;">
        _________________________________
        </p>
        <p style="margin: 0 0 1rem 0;">
        Bestellt am (*)/erhalten am (*) _________________
        </p>
        <p style="margin: 0 0 1rem 0;">
        Name des/der Verbraucher(s) _________________
        </p>
        <p style="margin: 0 0 1rem 0;">
        Anschrift des/der Verbraucher(s) _________________
        </p>
        <p style="margin: 0 0 1rem 0;">
        Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier) _________________
        </p>
        <p style="margin: 0;">
        Datum _________________<br><br>
        (*) Unzutreffendes streichen.
        </p>
        </div>
        </div>

        <h4 style="margin: 1.5rem 0 1rem 0; font-size: 1.1rem; font-weight: 600;">9d. Widerrufsbelehrung für die Erbringung von Dienstleistungen</h4>
        <div style="background: white; border: 2px solid black; padding: 1.5rem; margin-bottom: 1rem;">
        <p style="line-height: 1.7; margin: 0 0 1rem 0;">
        Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.<br>
        Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses.
        </p>
        <p style="line-height: 1.7; margin: 0 0 1rem 0;">
        Um Ihr Widerrufsrecht auszuüben, müssen Sie uns<br><br>
        <strong>BIGISOFT - Velizar Kolev</strong><br>
        Edith-Stein-Weg 9<br>
        64285 Darmstadt<br>
        E-Mail: info@d-luft.de<br>
        Tel: +49 (0) 151 145 19347<br><br>
        mittels einer eindeutigen Erklärung (z.B. ein mit der Post versandter Brief oder E-Mail) über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. Sie können dafür das beigefügte Muster-Widerrufsformular (unter Punkt 9c.) verwenden, das jedoch nicht vorgeschrieben ist.
        </p>
        <p style="line-height: 1.7; margin: 0;">
        Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung über die Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist absenden.
        </p>
        </div>

        <h4 style="margin: 1.5rem 0 1rem 0; font-size: 1.1rem; font-weight: 600;">Folgen des Widerrufs</h4>
        <div style="background: white; padding: 1rem; border: 2px solid black;">
        <p style="margin: 0 0 1rem 0; line-height: 1.7;">
        Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, einschließlich der Lieferkosten (mit Ausnahme der zusätzlichen Kosten, die sich daraus ergeben, dass Sie eine andere Art der Lieferung als die von uns angebotene, günstigste Standardlieferung gewählt haben), unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses Vertrags bei uns eingegangen ist. Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart; in keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.
        </p>
        <p style="margin: 0; line-height: 1.7;">
        Haben Sie verlangt, dass die Dienstleistungen während der Widerrufsfrist beginnen soll, so haben Sie uns einen angemessenen Betrag zu zahlen, der dem Anteil der bis zu dem Zeitpunkt, zu dem Sie uns von der Ausübung des Widerrufsrechts hinsichtlich dieses Vertrags unterrichten, bereits erbrachten Dienstleistungen im Vergleich zum Gesamtumfang der im Vertrag vorgesehenen Dienstleistungen entspricht.
        </p>
        </div>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">10. Gewährleistung</h3>
        
        <h4 style="margin: 1rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">11a. Gewährleistung für Verbraucher nach § 13 BGB</h4>
        <p>Es gilt die gesetzliche Gewährleistung / Mängelhaftung.</p>

        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">11b. Gewährleistung für Unternehmer nach § 14 BGB</h4>
        <p>Für alle Artikel gilt die gesetzliche Gewährleistung, sofern nicht ein längerer Zeitraum angegeben ist. Fehlmengen müssen innerhalb von 3 Tagen (Mo-Fr) angezeigt werden. Bei Transportschäden jeglicher Art, bitten wir Sie, diese bei dem Versandunternehmen innerhalb von 7 Tagen zu melden. Liegt ein von uns zu vertretender Mangel der Kaufsache vor, sind wir nach eigener Wahl zur Mängelbeseitigung oder gleichwertigen Ersatzlieferung berechtigt. Gutschriften erfolgen nach Anerkennung der Reklamation durch die BIGISOFT.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">11. Beschwerdeverfahren</h3>
        <p>Wir weisen Sie darauf hin, dass für die BIGISOFT keine rechtliche Verpflichtung zur Teilnahme an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle besteht und dass die BIGISOFT auch nicht bereit ist, an einem solchen Verfahren teilzunehmen.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">12. Speicherung des Vertragstextes</h3>
        <p>Wir speichern den Vertragstext und senden Ihnen die Bestelldaten und unsere AGB per E-Mail zu. Die AGB können Sie jederzeit auf unserer Internetseite einsehen.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">13. Gerichtsstand und anwendbares Recht</h3>
        <p>Für jeden mit der BIGISOFT abgeschlossenen Vertrag gilt ausschließlich das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechtes. Die Vertragssprache ist deutsch. Gerichtsstand für alle Streitigkeiten aus dem Vertragsverhältnis ist, wenn der Kunde Kaufmann, eine juristische Person des öffentlichen Rechts oder ein öffentlich-rechtliches Sondervermögen ist, Darmstadt. BIGISOFT ist auch berechtigt, am Hauptsitz des Auftraggebers zu klagen.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">14. Urheberrechte</h3>
        <p>Die Urheberrechte an der Gestaltung und an dem Inhalt dieser Website mit allen ihren Seiten verbleiben stets bei uns, insbesondere alle Fotografien, Texte, Beschreibungen sowie alle Inhalte. Das Kopieren und Herunterladen der Website oder Teilen (Bilder, Texte etc.) hiervon ist nicht gestattet. Die Vervielfältigung oder sonstige Nutzung für gewerbliche Zwecke, insbesondere die Weitergabe gegen Entgelt, ist nicht gestattet.</p>
        <p>Soweit wir durch Links oder Hyperlinks auf andere Websites oder Webpages verweisen, übernehmen wir keine Haftung oder Verantwortung für den Inhalt der Seiten.</p>
        </div>

        <div id="barrierefreiheit-section" style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">15. Erklärung zur Barrierefreiheit</h3>
        <p>Wir (BIGISOFT - Velizar Kolev) als Websitebetreiber sind bemüht, die Website in Einklang mit den einschlägigen Vorschriften zur Barrierefreiheit zu gestalten. Für uns gelten folgende Rechtsvorschriften:</p>
        <p>Landesgesetz zur Gleichstellung von Menschen mit Behinderungen (Landes-Behindertengleichstellungsgesetz - L-BGG) und L-BGG-Durchführungsverordnung</p>
        
        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">Website</h4>
        <p>Diese Erklärung zur Barrierefreiheit gilt für die Website https://www.d-luft.de/</p>
        
        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">Feedback und Kontaktangaben</h4>
        <p>Sind Ihnen Mängel beim barrierefreien Zugang zu Inhalten auf unserer Website aufgefallen oder haben Sie Anmerkungen sowie Fragen zum barrierefreien Zugang? Melden Sie sich gerne bei uns unter:</p>
        
        <div style="background: white; padding: 1rem; border: 1px solid black; margin-top: 1rem;">
        <p style="margin: 0.25rem 0; color: #333;"><strong>BIGISOFT</strong></p>
        <p style="margin: 0.25rem 0; color: #333;">Velizar Kolev</p>
        <p style="margin: 0.25rem 0; color: #333;">Edith-Stein-Weg 9</p>
        <p style="margin: 0.25rem 0; color: #333;">64285 Darmstadt</p>
        <p style="margin: 0.25rem 0; color: #333;">Email: info@d-luft.de</p>
        <p style="margin: 0.25rem 0; color: #333;">Tel.: +49 (0) 151 145 19347</p>
        </div>
        
        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">Weitergehende Maßnahmen zur Verbesserung des barrierefreien Zugangs zu unserer Website</h4>
        <p>Wir bemühen uns stets um die Verbesserung der barrierefreien Zugangsmöglichkeiten zu unserer Website. Derzeit tun wir hierzu Folgendes:</p>
        <p>Helfen Sie uns, die Zugänglichkeit zu verbessern - D-Luft optimiert kontinuierlich Bereiche unserer Webseiten und Applikationen. Bitte lassen Sie uns wissen, wenn Sie während Ihrer Nutzung dieser auf Barrieren stoßen.</p>
        
        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">Durchsetzungsverfahren</h4>
        <p>Sollten Sie der Ansicht sein, dass Sie durch eine nicht ausreichende barrierefreie Gestaltung unserer Website benachteiligt sind, können Sie sich an die zuständige Durchsetzungsstelle wenden. Diese erreichen Sie unter:</p>
        
        <div style="background: white; padding: 1rem; border: 1px solid black; margin-top: 1rem;">
        <p style="margin: 0.25rem 0; color: #333;"><strong>Hessisches Ministerium für Arbeit, Integration, Jugend und Soziales</strong></p>
        <p style="margin: 0.25rem 0; color: #333;">Sonnenberger Straße 2/2a</p>
        <p style="margin: 0.25rem 0; color: #333;">65193 Wiesbaden</p>
        <p style="margin: 0.25rem 0; color: #333;">Telefon: +49 611 3219-0</p>
        <p style="margin: 0.25rem 0; color: #333;">E-Mail: poststelle@hsm.hessen.de</p>
        </div>
        
        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">Erstellung dieser Erklärung zur Barrierefreiheit</h4>
        <p>Diese Erklärung wurde am 24.01.2026 erstellt.</p>
        <p>Die Erklärung wurde zuletzt am 24.01.2026 überprüft.</p>
        </div>

        <div style="margin-top: 3rem; padding-top: 2rem; border-top: 2px solid #ddd; text-align: center; color: #666; font-size: 0.9rem;">
        <p style="margin: 0.5rem 0;">Diese AGB wurden am 24.01.2026 erstellt.</p>
        </div>

        </div>
        </div>
        </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);
    }

    /**
    * Lock page scrolling when modal is open
    * Optimized to batch DOM reads before writes to avoid forced reflows
    */
    function lockPageScroll()
    {
        // Batch all reads first (before any DOM writes)
        const body = document.body;
        const scrollY = window.scrollY || window.pageYOffset || 0;

        // Batch all writes together
        requestAnimationFrame(() =>
        {
            body.style.position = 'fixed';
            body.style.top = `-${scrollY}px`;
            body.style.width = '100%';
            body.style.overflow = 'hidden';
            body.setAttribute('data-scroll-lock-y', scrollY);
        });
    }

    /**
    * Unlock page scrolling when modal is closed
    * Optimized to batch DOM reads and writes to avoid forced reflows
    */
    function unlockPageScroll()
    {
        const body = document.body;
        const html = document.documentElement;

        // Get stored scroll position before removing styles (batch read)
        const scrollY = parseInt(body.getAttribute('data-scroll-lock-y') || '0', 10);

        // Batch all writes together in requestAnimationFrame
        requestAnimationFrame(() =>
        {
            // Remove scroll lock styles
            body.style.position = '';
            body.style.top = '';
            body.style.width = '';
            body.style.overflow = '';
            html.style.overflow = '';

            // Remove the stored scroll position attribute
            body.removeAttribute('data-scroll-lock-y');

            // Restore scroll position in next frame to avoid forced reflow
            requestAnimationFrame(() =>
            {
                window.scrollTo(0, scrollY);
            });
        });
    }

    /**
    * Show confirmation dialog
    * @param {string} title - Dialog title
    * @param {string} message - Dialog message
    * @param {string} confirmText - Confirm button text
    * @param {string} cancelText - Cancel button text
    * @param {string} type - Dialog type (info, warning, error)
    * @returns {Promise<boolean>} - True if confirmed, false if cancelled
    */
    async function showConfirmationDialog(title, message, confirmText = 'Confirm', cancelText = 'Cancel', type = 'info')
    {
        return new Promise((resolve) =>
        {
            const modal = document.createElement('div');
            modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.8);
            z-index: 5000;
            display: flex;
            align-items: center;
            justify-content: center;
            `;

            const modalContent = document.createElement('div');
            modalContent.style.cssText = `
            background: white;
            border-radius: 15px;
            padding: 2rem;
            max-width: 500px;
            width: 90%;
            box-shadow: 0 20px 40px rgba(0,0,0,0.3);
            `;

            const iconMap = {
                info: 'ℹ️',
                warning: '⚠️',
                error: '❌',
                success: '✅'
            };

            const colorMap = {
                info: '#007bff',
                warning: '#ffc107',
                error: '#dc3545',
                success: '#28a745'
            };

            modalContent.innerHTML = `
            <div style="text-align: center;">
            <div style="font-size: 3rem; margin-bottom: 1rem;">${iconMap[type] || iconMap.info}</div>
            <h2 style="color: ${colorMap[type] || colorMap.info}; margin: 0 0 1rem 0;">${title}</h2>
            <div style="color: #666; margin-bottom: 2rem; line-height: 1.5;">${message}</div>
            <div style="display: flex; gap: 1rem; justify-content: center;">
            <button id="confirmBtn" style="background: ${colorMap[type] || colorMap.info}; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; font-weight: bold;">${confirmText}</button>
            <button id="cancelBtn" style="background: #6c757d; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; font-weight: bold;">${cancelText}</button>
            </div>
            </div>
            `;

            modal.appendChild(modalContent);
            document.body.appendChild(modal);

            // Lock page scrolling
            lockPageScroll();

            const confirmBtn = modal.querySelector('#confirmBtn');
            const cancelBtn = modal.querySelector('#cancelBtn');

            const closeModal = (result) =>
            {
                document.body.removeChild(modal);
                unlockPageScroll();
                resolve(result);
            };

            confirmBtn.onclick = () => closeModal(true);
            cancelBtn.onclick = () => closeModal(false);

            // Close modal when clicking outside
            modal.onclick = (e) =>
            {
                if (e.target === modal)
                {
                    closeModal(false);
                }
            };

            // Close modal with Escape key
            const handleKeyPress = (e) =>
            {
                if (e.key === 'Escape')
                {
                    closeModal(false);
                    document.removeEventListener('keydown', handleKeyPress);
                }
            };
            document.addEventListener('keydown', handleKeyPress);
        });
    }


    /**
    * Safely close modal with scroll unlock
    * @param {string|HTMLElement} modalOrId - Modal element or modal ID
    */
    function safeCloseModal(modalOrId)
    {
        let modal;

        if (typeof modalOrId === 'string')
        {
            modal = document.getElementById(modalOrId);
        }
        else
        {
            modal = modalOrId;
        }

        if (modal)
        {
            modal.remove();

            // Use requestAnimationFrame to ensure DOM is updated before checking for remaining modals
            requestAnimationFrame(() =>
            {
                // Check if any other modals are still visible using the getOpenModals function
                const remainingModals = getOpenModals();
                const hasVisibleModal = remainingModals.length > 0;

                // Only unlock scroll if no other modals are visible
                if (!hasVisibleModal && window.UIUtils && window.UIUtils.unlockPageScroll)
                {
                    window.UIUtils.unlockPageScroll();
                    // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔓 Page scroll unlocked - no modals remaining');
                }
            });
        }
    }

    // Export functions for use in other modules
    /**
     * Get all open modals with their z-index values
     * @returns {Array} Array of {element, zIndex, id} objects sorted by z-index (highest first)
     */
    function getOpenModals()
    {
        const modalIds = [
            'checkoutPage',
            'bankTransferModal',
            'cartModal',
            'invoiceModal',
            'paypalSummaryModal',
            'orderHistoryModal',
            'carDetailsModal',
            'carInfoModal',
            'orderDetailsModal',
            'editShippingAddressModal',
            'editProfileModal',
            'changePasswordModal',
            'paypalAccountModal',
            'imagePreviewModal',
            'cookieConsentModal',
            'gdprSettingsModal',
            'userModal'
        ];

        const openModals = [];

        // Check specific modal IDs
        for (const modalId of modalIds)
        {
            const modal = document.getElementById(modalId);
            if (modal && modal.style.display !== 'none' && window.getComputedStyle(modal).display !== 'none')
            {
                const zIndex = parseInt(window.getComputedStyle(modal).zIndex) || 0;
                openModals.push({ element: modal, zIndex: zIndex, id: modalId });
            }
        }

        // Also check for generic modal classes, but exclude the close button
        const genericModals = document.querySelectorAll('.modal, [class*="modal"], [id*="Modal"], [id*="modal"]');
        for (const modal of genericModals)
        {
            // Exclude the global close button and other non-modal elements
            if (modal.id === 'globalModalCloseBtn' || modal.id === 'userName' || modal.classList.contains('user-account-section') || modal.classList.contains('cart-icon'))
            {
                continue;
            }

            const computedStyle = window.getComputedStyle(modal);
            if (modal.style.display === 'none' || computedStyle.display === 'none' || computedStyle.visibility === 'hidden')
            {
                continue;
            }

            const zIndex = parseInt(computedStyle.zIndex) || 0;
            const position = computedStyle.position;

            // Only include elements that are actual modals (have high z-index and overlay positioning)
            if ((position === 'fixed' || position === 'absolute') && zIndex >= 1000)
            {
                // Check if not already added
                if (!openModals.find(m => m.element === modal))
                {
                    openModals.push({ element: modal, zIndex: zIndex, id: modal.id || 'unknown' });
                }
            }
        }

        // Sort by z-index descending (highest first)
        return openModals.sort((a, b) => b.zIndex - a.zIndex);
    }

    /**
     * Get the topmost (highest z-index) modal
     * @returns {Object|null} {element, zIndex, id} of topmost modal or null
     */
    function getTopmostModal()
    {
        const openModals = getOpenModals();
        return openModals.length > 0 ? openModals[0] : null;
    }

    /**
     * Close the topmost modal
     */
    function closeTopmostModal()
    {
        const topmost = getTopmostModal();
        if (!topmost)
        {
            console.warn('No topmost modal found to close');
            return;
        }

        const modalId = topmost.id;
        const modal = topmost.element;

        if (modal && typeof modal._closeModal === 'function')
        {
            try
            {
                modal._closeModal();
            }
            catch (error)
            {
                console.error('Error closing modal via instance handler:', error);
                if (modal && modal.parentNode)
                {
                    modal.parentNode.removeChild(modal);
                }
            }
            setTimeout(() =>
            {
                updateGlobalCloseButton();
            }, 50);
            return;
        }

        // Map modal IDs to their close functions
        const closeFunctions = {
            'cartModal': () =>
            {
                if (window.ShoppingCart && window.ShoppingCart.toggleCart)
                {
                    window.ShoppingCart.toggleCart();
                } else if (window.toggleCart)
                {
                    window.toggleCart();
                } else
                {
                    if (modal) modal.remove();
                }
            },
            'checkoutPage': () =>
            {
                if (window.closeCheckout)
                {
                    window.closeCheckout();
                } else
                {
                    if (modal) modal.remove();
                }
            },
            'bankTransferModal': () =>
            {
                if (window.closeBankTransferDialog)
                {
                    window.closeBankTransferDialog();
                } else
                {
                    if (modal) modal.remove();
                }
            },
            'invoiceModal': () =>
            {
                if (window.InvoiceGeneration && window.InvoiceGeneration.closeInvoice)
                {
                    window.InvoiceGeneration.closeInvoice();
                } else if (window.closeInvoice)
                {
                    window.closeInvoice();
                } else
                {
                    if (modal) modal.remove();
                }
            },
            'paypalSummaryModal': () =>
            {
                if (window.PayPalIntegration && window.PayPalIntegration.closePayPalSummaryModal)
                {
                    window.PayPalIntegration.closePayPalSummaryModal();
                } else if (window.closePayPalSummaryModal)
                {
                    window.closePayPalSummaryModal();
                } else
                {
                    if (modal) modal.remove();
                }
            },
            'paypalAccountModal': () =>
            {
                if (modal) modal.remove();
            },
            'orderHistoryModal': () =>
            {
                if (modal) modal.remove();
            },
            'carDetailsModal': () =>
            {
                if (modal) modal.remove();
            },
            'carInfoModal': () =>
            {
                if (modal)
                {
                    modal.remove();
                    // Unlock page scrolling
                    if (window.UIUtils && window.UIUtils.unlockPageScroll)
                    {
                        window.UIUtils.unlockPageScroll();
                    }
                }
            },
            'orderDetailsModal': () =>
            {
                if (modal)
                {
                    modal.remove();
                    // Unlock page scrolling
                    if (window.UIUtils && window.UIUtils.unlockPageScroll)
                    {
                        window.UIUtils.unlockPageScroll();
                    }
                }
            },
            'editShippingAddressModal': () =>
            {
                if (modal)
                {
                    modal.remove();
                    // Unlock page scrolling
                    if (window.UIUtils && window.UIUtils.unlockPageScroll)
                    {
                        window.UIUtils.unlockPageScroll();
                    }
                }
            },
            'editProfileModal': () =>
            {
                if (modal)
                {
                    modal.remove();
                    // Unlock page scrolling
                    if (window.UIUtils && window.UIUtils.unlockPageScroll)
                    {
                        window.UIUtils.unlockPageScroll();
                    }
                }
            },
            'changePasswordModal': () =>
            {
                if (modal)
                {
                    modal.remove();
                    // Unlock page scrolling
                    if (window.UIUtils && window.UIUtils.unlockPageScroll)
                    {
                        window.UIUtils.unlockPageScroll();
                    }
                }
            },
            'imagePreviewModal': () =>
            {
                // Call updateCarouselBeforeClose if it exists on the modal
                if (modal && typeof modal._updateCarouselBeforeClose === 'function')
                {
                    modal._updateCarouselBeforeClose();
                }
                if (window.UIUtils && window.UIUtils.safeCloseModal)
                {
                    window.UIUtils.safeCloseModal(modal);
                } else
                {
                    if (modal) modal.remove();
                }
            },
            'userModal': () =>
            {
                // Close user/login modal
                if (window.AccountManagement && window.AccountManagement.closeModal)
                {
                    window.AccountManagement.closeModal('userModal');
                } else if (window.closeModal)
                {
                    window.closeModal('userModal');
                } else
                {
                    if (modal) modal.remove();
                }
            },
            'cookieConsentModal': () =>
            {
                // Close cookie consent dialog using proper close function to restore scrolling
                if (window.CookieConsentManager && window.CookieConsentManager.closeConsentDialog)
                {
                    window.CookieConsentManager.closeConsentDialog(modal);
                } else
                {
                    // Fallback: manually restore scrolling and remove modal
                    if (window.UIUtils && window.UIUtils.unlockPageScroll)
                    {
                        window.UIUtils.unlockPageScroll();
                    }
                    if (modal) modal.remove();
                }
            },
            'gdprSettingsModal': () =>
            {
                // Close GDPR settings dialog using proper close function to restore scrolling
                if (window.CookieConsentManager && window.CookieConsentManager.closeGPDRSettings)
                {
                    window.CookieConsentManager.closeGPDRSettings(modal, false);
                } else
                {
                    // Fallback: manually restore scrolling and remove modal
                    if (window.UIUtils && window.UIUtils.unlockPageScroll)
                    {
                        window.UIUtils.unlockPageScroll();
                    }
                    if (modal) modal.remove();
                }
            }
        };

        // Call the appropriate close function
        const ensureScrollUnlock = () =>
        {
            const remainingModals = getOpenModals();
            const noVisibleModal = remainingModals.length === 0;

            if (noVisibleModal && window.UIUtils && typeof window.UIUtils.unlockPageScroll === 'function')
            {
                window.UIUtils.unlockPageScroll();
            }

            if (noVisibleModal && typeof window.fixScrollLock === 'function')
            {
                window.fixScrollLock();
            }
        };

        if (closeFunctions[modalId])
        {
            try
            {
                closeFunctions[modalId]();
            } catch (error)
            {
                console.error('Error closing modal:', error);
                // Fallback: just remove the modal
                if (modal)
                {
                    safeCloseModal(modal);
                }
            }
        }
        else
        {
            if (modal)
            {
                safeCloseModal(modal);
            }
        }

        // Update the close button after closing (with a small delay to ensure DOM updates)
        setTimeout(() =>
        {
            updateGlobalCloseButton();
        }, 50);

        requestAnimationFrame(() => ensureScrollUnlock());
        setTimeout(ensureScrollUnlock, 75);
    }

    /**
     * Check if any modal dialog is currently open
     * @returns {boolean} True if any modal is open, false otherwise
     */
    function isModalOpen()
    {
        return getOpenModals().length > 0;
    }

    /**
     * Create or update the global close button for the topmost modal
     */
    function updateGlobalCloseButton()
    {
        const topmost = getTopmostModal();
        let closeButton = document.getElementById('globalModalCloseBtn');
        const isMobile = window.innerWidth <= 768;

        if (topmost)
        {
            const modal = topmost.element;

            // Find the modal content element (the actual dialog box, not the overlay)
            let modalContent = modal.querySelector('.modal-content, .modal-dialog-content, [class*="modal-content"], [class*="modalContent"]');
            // If no modal-content found, try to find a child div that's not the overlay
            if (!modalContent)
            {
                const children = Array.from(modal.children);
                modalContent = children.find(child =>
                    child.tagName === 'DIV' &&
                    !child.classList.contains('modal-dialog-overlay') &&
                    window.getComputedStyle(child).position !== 'absolute'
                ) || modal;
            }

            // Ensure modal content has relative positioning for absolute child positioning
            if (!isMobile && modalContent !== modal)
            {
                const currentPosition = window.getComputedStyle(modalContent).position;
                if (currentPosition === 'static')
                {
                    modalContent.style.position = 'relative';
                }
            }

            // Calculate position
            let topPos, rightPos, positionType;

            if (isMobile)
            {
                // Mobile: Fixed to screen upper right corner
                positionType = 'fixed';
                topPos = '20px'; /* Upper right corner */
                rightPos = '20px'; /* Upper right corner */
            } else
            {
                // Desktop: Absolute positioning inside modal content, overlapping upper right corner
                positionType = 'absolute';
                topPos = '10px';
                rightPos = '10px';
            }

            const modalComputedStyle = window.getComputedStyle(modal);
            const topmostZIndex = topmost.zIndex || parseInt(modalComputedStyle.zIndex) || 0;
            const newZIndex = Math.max(10001, topmostZIndex + 1000);

            // Create button if it doesn't exist
            if (!closeButton)
            {
                closeButton = document.createElement('button');
                closeButton.id = 'globalModalCloseBtn';
                closeButton.type = 'button'; // Prevent form submission
                closeButton.innerHTML = '&times;';
                closeButton.setAttribute('aria-label', 'Close dialog');
                closeButton.style.cssText = `
                position: ${positionType};
                top: ${topPos};
                right: ${rightPos};
                background: rgba(255,255,255,0.95);
                border: 2px solid #667eea;
                border-radius: 50%;
                width: 50px;
                height: 50px;
                font-size: 32px;
                cursor: pointer;
                color: #667eea;
                z-index: ${newZIndex};
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                transition: all 0.2s ease;
                pointer-events: auto;
                `;
                closeButton.addEventListener('mouseenter', function ()
                {
                    this.style.background = '#667eea';
                    this.style.color = 'white';
                    this.style.transform = 'scale(1.1)';
                });
                closeButton.addEventListener('mouseleave', function ()
                {
                    this.style.background = 'rgba(255,255,255,0.95)';
                    this.style.color = '#667eea';
                    this.style.transform = 'scale(1)';
                });
                closeButton.addEventListener('click', function (e)
                {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    closeTopmostModal();
                    return false;
                }, true); // Use capture phase

                // Append to modal content on desktop, body on mobile
                if (isMobile)
                {
                    document.body.appendChild(closeButton);
                } else
                {
                    modalContent.appendChild(closeButton);
                }

                closeButton.style.setProperty('position', positionType, 'important');
                closeButton.style.setProperty('top', topPos, 'important');
                closeButton.style.setProperty('right', rightPos, 'important');
                closeButton.style.setProperty('z-index', String(newZIndex), 'important');
                closeButton.style.setProperty('pointer-events', 'auto', 'important');
            }
            else
            {
                // Remove from current parent if it exists
                if (closeButton.parentElement)
                {
                    closeButton.parentElement.removeChild(closeButton);
                }

                // Update position and z-index
                closeButton.style.setProperty('position', positionType, 'important');
                closeButton.style.setProperty('top', topPos, 'important');
                closeButton.style.setProperty('right', rightPos, 'important');

                // Update z-index to be above the topmost modal - always use a very high z-index
                const currentZIndex = parseInt(window.getComputedStyle(closeButton).zIndex) || 0;
                // Always use a z-index higher than any modal (at least 10001, or topmost + 1000 to ensure it's always on top)

                // Only update if z-index actually changed to avoid unnecessary updates
                if (currentZIndex !== newZIndex)
                {
                    closeButton.style.setProperty('z-index', String(newZIndex), 'important');
                }
                closeButton.style.setProperty('pointer-events', 'auto', 'important');
                closeButton.style.setProperty('display', 'flex', 'important');

                // Append to correct parent
                if (isMobile)
                {
                    document.body.appendChild(closeButton);
                } else
                {
                    modalContent.appendChild(closeButton);
                }
            }
        }
        else
        {
            // Remove button if no modals are open
            if (closeButton)
            {
                closeButton.remove();
            }
        }
    }

    /**
     * Update header elements z-index and pointer-events based on modal state
     */
    function updateHeaderElementsZIndex()
    {
        // Cache window.innerWidth once to avoid forced reflow
        const isMobile = window.innerWidth <= 768;
        const modalOpen = isModalOpen();

        // Batch all DOM queries
        const userAccountSection = document.querySelector('.user-account-section');
        const cartIcon = document.querySelector('.cart-icon');
        const userName = document.getElementById('userName');
        const userIconContainer = document.querySelector('.user-icon-container');
        const userIcon = document.querySelector('.user-icon');

        if (modalOpen)
        {
            // Lower z-index when modal is open (below modals which are 3000-5000)
            // Disable pointer events to prevent clicking
            // Use less aggressive opacity on mobile to avoid rendering issues
            const opacityValue = isMobile ? '0.5' : '0.4';

            if (userAccountSection)
            {
                userAccountSection.style.setProperty('z-index', '2000', 'important');
                userAccountSection.style.setProperty('pointer-events', 'none', 'important');
                // Ensure background is visible even with reduced opacity
                userAccountSection.style.setProperty('backdrop-filter', 'blur(5px)', 'important');
            }
            if (cartIcon)
            {
                cartIcon.style.setProperty('z-index', '2000', 'important');
                cartIcon.style.setProperty('pointer-events', 'none', 'important');
                cartIcon.style.setProperty('opacity', opacityValue, 'important');
                // Keep background transparent when modal is open
                cartIcon.style.setProperty('background-color', 'transparent', 'important');
            }
            if (userName)
            {
                userName.style.setProperty('z-index', '2000', 'important');
                userName.style.setProperty('opacity', opacityValue, 'important');
                userName.style.setProperty('color', isMobile ? '#666' : '#999', 'important');
                // Ensure background is visible
                userName.style.setProperty('background', 'rgba(255, 255, 255, 0.8)', 'important');
            }
            if (userIconContainer)
            {
                userIconContainer.style.setProperty('pointer-events', 'none', 'important');
            }
            if (userIcon)
            {
                userIcon.style.setProperty('pointer-events', 'none', 'important');
                userIcon.style.setProperty('opacity', opacityValue, 'important');
                // Keep background transparent when modal is open
                userIcon.style.setProperty('background-color', 'transparent', 'important');
            }
        }
        else
        {
            // Restore original z-index and enable pointer events when no modal is open
            if (userAccountSection)
            {
                userAccountSection.style.setProperty('z-index', '10001', 'important');
                userAccountSection.style.setProperty('pointer-events', 'auto', 'important');
                userAccountSection.style.removeProperty('backdrop-filter');
            }
            if (cartIcon)
            {
                cartIcon.style.setProperty('z-index', '10001', 'important');
                cartIcon.style.setProperty('pointer-events', 'auto', 'important');
                cartIcon.style.setProperty('opacity', '1', 'important');
                cartIcon.style.removeProperty('background-color');
            }
            if (userName)
            {
                userName.style.setProperty('z-index', '10001', 'important');
                userName.style.setProperty('opacity', '1', 'important');
                userName.style.removeProperty('color');
                userName.style.removeProperty('background');
            }
            if (userIconContainer)
            {
                userIconContainer.style.setProperty('pointer-events', 'auto', 'important');
            }
            if (userIcon)
            {
                userIcon.style.setProperty('pointer-events', 'auto', 'important');
                userIcon.style.setProperty('opacity', '1', 'important');
                userIcon.style.removeProperty('background-color');
            }
        }
    }

    // Monitor modal state and update header elements
    let modalCheckInterval = null;
    let lastModalState = null;
    let lastTopmostModalId = null;

    function startModalStateMonitoring()
    {
        // Check immediately
        updateHeaderElementsZIndex();
        updateGlobalCloseButton();

        // Set up interval to check modal state periodically
        if (modalCheckInterval)
        {
            clearInterval(modalCheckInterval);
        }

        modalCheckInterval = setInterval(() =>
        {
            const currentModals = getOpenModals();
            const currentTopmost = currentModals.length > 0 ? currentModals[0] : null;
            const currentTopmostId = currentTopmost ? currentTopmost.id : null;
            const hasModals = currentModals.length > 0;

            // Only update if modal state actually changed
            if (hasModals !== lastModalState || currentTopmostId !== lastTopmostModalId)
            {
                updateHeaderElementsZIndex();
                updateGlobalCloseButton();
                lastModalState = hasModals;
                lastTopmostModalId = currentTopmostId;
            }
        }, 200); // Check every 200ms (reduced frequency)

        // Also listen for DOM changes (when modals are added/removed)
        let observerTimeout = null;
        const observer = new MutationObserver(() =>
        {
            // Debounce observer updates to avoid excessive calls
            if (observerTimeout)
            {
                clearTimeout(observerTimeout);
            }
            observerTimeout = setTimeout(() =>
            {
                const currentModals = getOpenModals();
                const currentTopmost = currentModals.length > 0 ? currentModals[0] : null;
                const currentTopmostId = currentTopmost ? currentTopmost.id : null;
                const hasModals = currentModals.length > 0;

                // Only update if modal state actually changed
                if (hasModals !== lastModalState || currentTopmostId !== lastTopmostModalId)
                {
                    updateHeaderElementsZIndex();
                    updateGlobalCloseButton();
                    lastModalState = hasModals;
                    lastTopmostModalId = currentTopmostId;
                }
            }, 100); // Debounce to 100ms
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'id', 'class']
        });

        // Update close button position on window resize (especially important for desktop positioning)
        let resizeTimeout = null;
        window.addEventListener('resize', () =>
        {
            if (resizeTimeout)
            {
                clearTimeout(resizeTimeout);
            }
            resizeTimeout = setTimeout(() =>
            {
                updateGlobalCloseButton();
            }, 100); // Debounce resize events
        });
    }

    /**
     * Format a date in Germany timezone (Europe/Berlin) using German format (DD.MM.YYYY)
     * Always uses Germany timezone regardless of client's location
     * @param {string|Date|number} dateInput - Date string (ISO), Date object, or timestamp
     * @returns {string} Formatted date string or 'N/A' if invalid
     */
    function formatDate(dateInput)
    {
        if (!dateInput) return 'N/A';

        try
        {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) return 'N/A';

            // Always use Germany timezone (Europe/Berlin) for consistent display
            // 'de-DE' locale provides German format (DD.MM.YYYY)
            return date.toLocaleDateString('de-DE', {
                timeZone: 'Europe/Berlin',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        }
        catch (error)
        {
            return 'N/A';
        }
    }

    /**
     * Format a time in Germany timezone (Europe/Berlin) using German format (HH:MM)
     * Always uses Germany timezone regardless of client's location
     * @param {string|Date|number} dateInput - Date string (ISO), Date object, or timestamp
     * @returns {string} Formatted time string or 'N/A' if invalid
     */
    function formatTime(dateInput)
    {
        if (!dateInput) return 'N/A';

        try
        {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) return 'N/A';

            // Always use Germany timezone (Europe/Berlin) for consistent display
            return date.toLocaleTimeString('de-DE', {
                timeZone: 'Europe/Berlin',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        catch (error)
        {
            return 'N/A';
        }
    }

    /**
     * Format a date and time in Germany timezone (Europe/Berlin) using German format (DD.MM.YYYY HH:MM)
     * Always uses Germany timezone regardless of client's location
     * @param {string|Date|number} dateInput - Date string (ISO), Date object, or timestamp
     * @returns {string} Formatted date and time string or 'N/A' if invalid
     */
    function formatDateTime(dateInput)
    {
        if (!dateInput) return 'N/A';

        try
        {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) return 'N/A';

            // Always use Germany timezone (Europe/Berlin) for consistent display
            return date.toLocaleString('de-DE', {
                timeZone: 'Europe/Berlin',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        catch (error)
        {
            return 'N/A';
        }
    }

    /**
     * Format a date and time separately (for display with space between)
     * @param {string|Date|number} dateInput - Date string (ISO), Date object, or timestamp
     * @returns {Object} Object with date and time properties
     */
    function formatDateAndTime(dateInput)
    {
        return {
            date: formatDate(dateInput),
            time: formatTime(dateInput),
            datetime: formatDateTime(dateInput)
        };
    }

    window.UIUtils = {
        showNotification,
        closeModal,
        safeCloseModal,
        handleNavClick,
        hideAllSections,
        showAllSections,
        scrollToSectionWithOffset,
        scrollToContact,
        isDashboardOpen,
        hideDashboard,
        showDashboard,
        initializeUIUtils,
        showPrivacyPolicy,
        showImpressum,
        showAGB,
        showWiderruf,
        showBarrierefreiheit,
        openMobileMenu,
        closeMobileMenu,
        updateMobileMenuVisibility,
        lockPageScroll,
        unlockPageScroll,
        showConfirmationDialog,
        getHeaderHeight,
        updateNavigationHighlight,
        handleScroll,
        highlightSection,
        isModalOpen,
        updateHeaderElementsZIndex,
        startModalStateMonitoring,
        getTopmostModal,
        closeTopmostModal,
        updateGlobalCloseButton,
        updateModalHeaderSpacing,
        formatDate,
        formatTime,
        formatDateTime,
        formatDateAndTime
    };

    // Override the fallback with the real function
    window.handleNavClick = handleNavClick;

    // Global function to fix any stuck scroll locks
    window.fixScrollLock = function ()
    {
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
        }
    };

    /**
    * Get the height of the fixed header/menu
    * @returns {number} Height of the fixed header in pixels
    */
    function getHeaderHeight()
    {
        const header = document.querySelector('header');
        if (header)
        {
            return header.offsetHeight;
        }
        return 0; // Fallback if header not found
    }


    /**
    * Immediately highlight a specific section
    * @param {string} sectionId - The ID of the section to highlight
    */
    function highlightSection(sectionId)
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log(`🎯 Immediately highlighting section: ${sectionId}`);

        // Define sections and their corresponding navigation links
        const sections = [
            { id: 'home', navSelector: 'a[href="#home"]' },
            { id: 'uber', navSelector: 'a[href="#uber"]' },
            { id: 'services', navSelector: 'a[href="#services"]' },
            { id: 'webshop', navSelector: 'a[href="#webshop"]' },
            { id: 'contact', navSelector: 'a[href="#contact"]' },
            { id: 'dashboard', navSelector: '#navDashboard, #mobileNavDashboard' }
        ];

        // Find the section to highlight
        const sectionToHighlight = sections.find(section => section.id === sectionId);

        if (sectionToHighlight)
        {
            // Remove active class from ALL possible navigation links FIRST
            const allNavLinks = document.querySelectorAll('.nav-links a, .mobile-nav a, #navDashboard, #mobileNavDashboard, .dashboard-tab');
            allNavLinks.forEach(link =>
            {
                link.classList.remove('active');
            });

            // Add active class to the selected section's navigation link
            const navLinks = document.querySelectorAll(sectionToHighlight.navSelector);
            navLinks.forEach(link =>
            {
                link.classList.add('active');
            });

            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log(`✅ Immediately highlighted: ${sectionToHighlight.id}`);
        }
        else
        {
            console.warn(`⚠️ Section not found for highlighting: ${sectionId}`);
        }
    }

    /**
    * Smart navigation highlighting based on which section is at the top of the viewport
    * Highlights the menu item corresponding to the section that's currently at the top
    */
    function updateNavigationHighlight()
    {
        // If dashboard is visible, highlight dashboard menu item
        if (isDashboardVisible)
        {
            // Remove active class from ALL possible navigation links FIRST
            const allNavLinks = document.querySelectorAll('.nav-links a, .mobile-nav a, #navDashboard, #mobileNavDashboard, .dashboard-tab');
            allNavLinks.forEach(link =>
            {
                link.classList.remove('active');
            });

            // Add active class to dashboard navigation links
            const dashboardLinks = document.querySelectorAll('#navDashboard, #mobileNavDashboard');
            dashboardLinks.forEach(link =>
            {
                link.classList.add('active');
            });

            return;
        }

        // Use requestAnimationFrame to batch layout reads
        requestAnimationFrame(() =>
        {
            // Define sections and their corresponding navigation links
            const sections = [
                { id: 'home', navSelector: 'a[href="#home"]' },
                { id: 'uber', navSelector: 'a[href="#uber"]' },
                { id: 'services', navSelector: 'a[href="#services"]' },
                { id: 'webshop', navSelector: 'a[href="#webshop"]' },
                { id: 'contact', navSelector: 'a[href="#contact"]' }
            ];

            // Batch all layout reads together
            const headerHeight = getHeaderHeight();
            const viewportTop = headerHeight;

            // Collect all section elements and their rects in one batch
            const sectionRects = [];
            sections.forEach(section =>
            {
                const element = document.getElementById(section.id);
                if (element && element.style.display !== 'none')
                {
                    const rect = element.getBoundingClientRect();
                    sectionRects.push({
                        section: section,
                        element: element,
                        rect: rect,
                        elementTop: rect.top,
                        elementBottom: rect.bottom
                    });
                }
            });

            // Find the section that's currently at the top of the viewport
            let topSection = null;
            let closestDistance = Infinity;
            const bufferZone = 20;

            sectionRects.forEach(({ section, elementTop, elementBottom }) =>
            {
                const isAtTop = elementTop <= viewportTop && elementBottom > viewportTop;
                const isNearTop = elementTop <= (viewportTop + bufferZone) && elementBottom > viewportTop;

                if (isAtTop || isNearTop)
                {
                    const distanceFromTop = Math.abs(elementTop - viewportTop);

                    if (distanceFromTop < closestDistance)
                    {
                        closestDistance = distanceFromTop;
                        topSection = section;
                    }
                }
            });

            if (topSection)
            {
                // Always highlight the new section at top (replace any previous highlight)

                // Remove active class from ALL possible navigation links FIRST
                const allNavLinks = document.querySelectorAll('.nav-links a, .mobile-nav a, #navDashboard, #mobileNavDashboard, .dashboard-tab');
                allNavLinks.forEach(link =>
                {
                    link.classList.remove('active');
                });

                // Add active class to the selected section's navigation link
                const navLinks = document.querySelectorAll(topSection.navSelector);
                navLinks.forEach(link =>
                {
                    link.classList.add('active');
                });
            }
        });
    }

    /**
    * Throttled scroll handler for performance
    */
    let scrollTimeout;
    function handleScroll()
    {
        if (scrollTimeout)
        {
            clearTimeout(scrollTimeout);
        }

        scrollTimeout = setTimeout(() =>
        {
            updateNavigationHighlight();
        }, 10); // Throttle to 10ms for smooth performance
    }

    // Add scroll event listener for navigation highlighting
    window.addEventListener('scroll', handleScroll);

    /**
     * Check hide_webshop setting and hide/show webshop elements accordingly
     */
    async function updateWebshopVisibility()
    {
        try
        {
            const response = await fetch('/api/public/settings/hide_webshop_enabled');
            if (!response.ok)
            {
                console.warn('Failed to fetch hide_webshop setting, defaulting to visible');
                return; // Default to visible if we can't fetch the setting
            }

            const data = await response.json();
            const hideWebshop = data.setting && data.setting.value === true;

            // Hide/show webshop navigation link (desktop)
            const webshopNavLinks = document.querySelectorAll('.nav-links a[href="#webshop"]');
            webshopNavLinks.forEach(link =>
            {
                const listItem = link.closest('li');
                if (listItem)
                {
                    listItem.style.display = hideWebshop ? 'none' : '';
                }
            });

            // Hide/show webshop navigation link (mobile)
            const webshopMobileNavLinks = document.querySelectorAll('.mobile-nav a[href="#webshop"]');
            webshopMobileNavLinks.forEach(link =>
            {
                const listItem = link.closest('li');
                if (listItem)
                {
                    listItem.style.display = hideWebshop ? 'none' : '';
                }
            });

            // Hide/show "Jetzt bestellen" button
            const jetztBestellenButtons = document.querySelectorAll('a[href="#webshop"], .btn[onclick*="webshop"]');
            jetztBestellenButtons.forEach(button =>
            {
                // Check if button text contains "Jetzt bestellen" or if it links to webshop
                if (button.textContent.includes('Jetzt bestellen') || button.getAttribute('onclick')?.includes('webshop'))
                {
                    button.style.display = hideWebshop ? 'none' : '';
                }
            });

            // Hide/show cart button
            const cartIcon = document.querySelector('.cart-icon');
            if (cartIcon)
            {
                cartIcon.style.display = hideWebshop ? 'none' : '';
            }
        } catch (error)
        {
            console.warn('Error checking hide_webshop setting:', error);
            // Default to visible on error
        }
    }

    // Initialize navigation highlighting on page load
    document.addEventListener('DOMContentLoaded', function ()
    {
        // Check and update webshop visibility
        updateWebshopVisibility();

        // Highlight the Leistungen (services) section initially
        const servicesSection = document.getElementById('services');
        if (servicesSection)
        {
            // Remove active class from all navigation links
            const allNavLinks = document.querySelectorAll('.nav-links a, .mobile-nav a');
            allNavLinks.forEach(link =>
            {
                link.classList.remove('active');
            });

            // Add active class to the services section's navigation link
            const servicesNavLinks = document.querySelectorAll('a[href="#services"]');
            servicesNavLinks.forEach(link =>
            {
                link.classList.add('active');
            });

            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('✅ Initial highlight set to services section: Leistungen');
        }

        // Then update based on scroll position
        updateNavigationHighlight();
    });

    // Expose updateWebshopVisibility globally so it can be called when settings change
    window.updateWebshopVisibility = updateWebshopVisibility;

    /**
    * Show Rechtliche Hinweise modal
    */
    function showRechtlicheHinweise()
    {
        // Cache window.innerWidth once to avoid forced reflow
        const isMobile = window.innerWidth <= 768;

        // Remove any existing modal
        const existingModal = document.getElementById('rechtlicheHinweiseModal');
        if (existingModal)
        {
            existingModal.remove();
        }

        const modalHTML = `
        <div id="rechtlicheHinweiseModal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: ${isMobile ? '#000000' : 'rgba(0, 0, 0, 0.8)'}; z-index: ${isMobile ? '10001' : '11000'}; display: flex; align-items: center; justify-content: center; padding: 2rem;">
        <div style="background: white; border-radius: 15px; padding: 0; max-width: 1200px; width: 100%; max-height: 90vh; overflow: hidden; box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3); display: flex; flex-direction: column;">
        <div style="background: black; color: white; padding: 1.5rem 2rem; border-radius: 15px 15px 0 0; display: flex; justify-content: space-between; align-items: center;">
        <h2 style="margin: 0; font-size: 1.5rem; font-weight: 600;">⚖️ Rechtliche Hinweise</h2>
        <button onclick="document.getElementById('rechtlicheHinweiseModal').remove();" style="background: none; border: none; color: white; font-size: 1.5rem; cursor: pointer; padding: 0; line-height: 1;">&times;</button>
        </div>
        <div style="padding: 2rem; overflow-y: auto; flex: 1; line-height: 1.6;">
        
        <div style="margin-bottom: 2rem;">
        <h3 style="margin: 0 0 1rem 0; font-size: 1.25rem; font-weight: 600; border-bottom: 2px solid black; padding-bottom: 0.5rem;">⚖️ Rechtliche Hinweise zum Einbau von ECU Software-Tuning</h3>
        </div>

        <div style="margin-bottom: 2rem;">
        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">📋 Abnahme- und Eintragungspflicht für Chiptuning in den Fahrzeugpapieren</h4>
        <p>Durch Chiptuning erlischt die Allgemeine Betriebserlaubnis des Fahrzeugs, wenn die Abnahme des Einbaus der Leistungssteigerung nicht umgehend durch eine amtlich anerkannte Prüfeinrichtung i.S.d. § 19 StVZO durchgeführt und entsprechend § 22 StVZO bestätigt worden ist, d.h. die Leistungssteigerung muss in die Fahrzeugpapiere eingetragen werden.</p>
        <p>Eine Eintragung in die Fahrzeugpapiere ist über eine kostenpflichtige Einzelabnahme bei einem anerkannten Prüfinstitut möglich. Da der genaue Ablauf und die Kosten für eine Einzelabnahme von Fall zu Fall unterschiedlich sind, sollten Sie sich direkt beim entsprechenden Prüfinstitut über die Details der Einzelabnahme in Ihrem Fall informieren.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">🔧 Auswirkungen auf Garantieansprüche und Gewährleistungen</h4>
        <p>Durch den Einbau eines D-Luft Performance Software verlieren Sie als Kunde Garantieansprüche auf den Motor gegenüber dem Garantiegeber für Ihr Fahrzeug.</p>
        <p>Der Einbau der D-Luft Performance Software kann Ihre Gewährleistungsansprüche gegenüber dem Fahrzeugverkäufer in Bezug auf das veränderte Fahrzeug beeinträchtigen.</p>
        </div>

        <div style="margin-bottom: 2rem;">
        <h4 style="margin: 1.5rem 0 0.5rem 0; font-size: 1.1rem; font-weight: 600;">🛡️ Versicherungsschutz</h4>
        <p>Der Einbau der D-Luft Performance Software kann den Versicherungsschutz Ihres Fahrzeugs beeinträchtigen. Sie müssen deshalb Ihren Fahrzeugversicherer vor Einbau benachrichtigen.</p>
        </div>

        <div style="background: #fff3cd; padding: 1.5rem; border-radius: 8px; border-left: 4px solid #ffc107; margin: 2rem 0;">
        <p style="margin: 0; color: #856404; font-weight: 600;">⚠️ Wichtiger Hinweis:</p>
        <p style="margin: 0.5rem 0 0 0; color: #856404;">Diese Informationen dienen nur der allgemeinen Orientierung und ersetzen keine individuelle rechtliche Beratung. Wenden Sie sich bei Fragen an einen Rechtsanwalt oder an die zuständigen Behörden.</p>
        </div>

        </div>
        </div>
        </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);
    }

    // Expose modal functions globally so they can be called from HTML onclick handlers
    window.showPrivacyPolicy = showPrivacyPolicy;
    window.showImpressum = showImpressum;
    window.showWiderruf = showWiderruf;
    window.showAGB = showAGB;
    window.showBarrierefreiheit = showBarrierefreiheit;
    window.showRechtlicheHinweise = showRechtlicheHinweise;

})();
